// Third-party liquidity provider integration with live API endpoints
import { toast } from 'sonner';
import { signWeb3Transaction } from './walletConnectors';
import { getTokenAddress as getTokenAddressFromRegistry, getTokenInfo } from './tokenRegistry';
import { isAtomicSwapCompatible } from './atomicSwapUtils';
import { wormhole } from '@wormhole-foundation/sdk';
import * as evm from '@wormhole-foundation/sdk-evm';
import { createAcrossClient } from '@across-protocol/app-sdk';
import { formatUnits, parseUnits } from 'viem';
import { mainnet, base, arbitrum } from 'viem/chains';
import { 
  fetchICPChainFusionQuote, 
  isICPChainFusionAvailable,
  getICPChainFusionPathway 
} from './icpChainFusion';

export interface LiquidityProvider {
  name: string;
  fee: number;
  apiEndpoint: string;
  capabilities?: {
    supportsUTXO?: boolean;  // Can handle RVN, BTC, LTC, DOGE, etc.
    supportsEVM?: boolean;    // Can handle ERC-20 tokens on EVM chains
    supportedChains?: number[]; // Which chains are supported
  };
}

export interface SwapQuote {
  provider: string;
  rate: number;
  fee: number; // Total fee (provider fee + KawSwap DEX fee)
  estimatedOutput: number;
  available: boolean;
  error?: string;
  minAmount?: number; // Minimum swap amount required (for deposit_too_small errors)
  // Fee breakdown for transparency
  providerFee?: number; // Provider's base fee
  kawSwapFee?: number; // KawSwap DEX fee (0.30%)
  // Gas estimates for transparency
  gasEstimate?: {
    sourceChain: {
      gasLimit?: string;
      gasPrice?: string;
      estimatedCost: number; // in native token (ETH, etc.)
      estimatedCostUSD?: number; // USD value
    };
    destinationChain?: {
      relayerFee?: number; // fee paid to relayer (includes destination gas)
      estimatedCostUSD?: number;
    };
    totalGasCostUSD?: number; // total gas cost in USD
  };
  totalCost?: number; // amount + fees + gas (in output token)
  isCrossChain?: boolean; // indicates if this is a cross-chain swap
  // KYC risk indicator
  kycRisk?: 'low' | 'medium' | 'high';
  kycMessage?: string;
}

// API request timeout
const API_TIMEOUT = 15000;

// VPS/provider proxy base URL (recommended for providers blocked by CORS, and to keep API keys server-side)
// Example: https://rpc.kawswap.io/providers  OR  https://api.kawswap.io/providers
const PROVIDER_PROXY_BASE_URL: string = (import.meta as any).env?.VITE_PROVIDER_PROXY_URL || '';

// KawSwap DEX fee (0.40% = 0.004)
// Applied to ICP and 1inch swaps (direct fee collection)
// NOT applied to affiliate swaps (SimpleSwap, ChangeNOW) - they pay 0.4% commission
const KAWSWAP_DEX_FEE = 0.004;

/**
 * Apply KawSwap DEX fee to a quote
 * @param providerFee - Provider's base fee
 * @param estimatedOutput - Estimated output before DEX fee
 * @param isAffiliateProvider - True if this is an affiliate provider (SimpleSwap, ChangeNOW)
 * @returns Object with total fee, provider fee, KawSwap fee, and adjusted output
 */
function applyKawSwapFee(
  providerFee: number, 
  estimatedOutput: number,
  isAffiliateProvider: boolean = false
): {
  totalFee: number;
  providerFee: number;
  kawSwapFee: number;
  adjustedOutput: number;
} {
  // For affiliate providers (SimpleSwap, ChangeNOW):
  // We earn 0.4% commission automatically, so DON'T add markup
  // For direct providers (1inch, ICP): Fee is already included in execution
  const kawSwapFee = isAffiliateProvider ? 0.0 : KAWSWAP_DEX_FEE;
  const totalFee = providerFee + kawSwapFee;
  
  // Only adjust output if we're adding a markup
  const adjustedOutput = isAffiliateProvider ? estimatedOutput : estimatedOutput * (1 - kawSwapFee);
  
  return {
    totalFee,
    providerFee,
    kawSwapFee: isAffiliateProvider ? providerFee : kawSwapFee, // Show affiliate fee as kawSwapFee
    adjustedOutput,
  };
}

// Gas price cache (1 minute TTL)
const gasPriceCache = new Map<string, { price: number; timestamp: number }>();
const GAS_CACHE_TTL = 60000; // 1 minute

// Request deduplication cache - prevents duplicate in-flight requests
// Industry standard practice to reduce redundant API calls
const inFlightRequests = new Map<string, Promise<any>>();

/**
 * Generate cache key for request deduplication
 */
function getRequestCacheKey(provider: string, fromToken: string, toToken: string, amount: number, ...extraParams: (string | number | undefined)[]): string {
  const extra = extraParams.filter(p => p !== undefined).map(p => String(p)).join(':');
  return `${provider}:${fromToken}:${toToken}:${amount}${extra ? ':' + extra : ''}`;
}

/**
 * Request deduplication wrapper - reuses in-flight promises for identical requests
 * Industry standard practice to prevent duplicate API calls
 */
async function fetchWithDedup<T>(
  cacheKey: string,
  fetchFn: () => Promise<T>
): Promise<T> {
  // Check if identical request is already in-flight
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey)!;
  }
  
  // Create new request promise
  const promise = fetchFn().finally(() => {
    // Clean up after request completes (success or failure)
    inFlightRequests.delete(cacheKey);
  });
  
  // Store promise for deduplication
  inFlightRequests.set(cacheKey, promise);
  return promise;
}

// SDK initialization cache
let wormholeSDK: any = null;
let acrossSDK: any = null;
const SDK_INIT_PROMISE: { wormhole?: Promise<any>; across?: Promise<any> } = {};

/**
 * Initialize Wormhole SDK (cached)
 */
async function getWormholeSDK() {
  if (wormholeSDK) return wormholeSDK;
  
  if (!SDK_INIT_PROMISE.wormhole) {
    SDK_INIT_PROMISE.wormhole = (async () => {
      try {
        const wh = await wormhole('Mainnet', [evm]);
        wormholeSDK = wh;
        return wh;
      } catch (error) {
        console.error('Failed to initialize Wormhole SDK:', error);
        return null;
      }
    })();
  }
  
  return SDK_INIT_PROMISE.wormhole;
}

/**
 * Initialize Across SDK (cached, requires integratorId)
 * 
 * To get an integratorId:
 * 1. Go to https://docs.across.to
 * 2. Fill out the integrator form
 * 3. Add VITE_ACROSS_INTEGRATOR_ID=your_id_here to your .env file
 * 
 * Until you have an integratorId, Across will use fallback estimation
 */
async function getAcrossSDK(integratorId?: string) {
  if (acrossSDK) return acrossSDK;
  
  // If no integratorId, return null (user needs to get one)
  if (!integratorId) {
    const envIntegratorId = import.meta.env.VITE_ACROSS_INTEGRATOR_ID;
    if (!envIntegratorId) {
      // Silently return null - fallback estimation will be used
      return null;
    }
    integratorId = envIntegratorId;
  }
  
  if (!SDK_INIT_PROMISE.across) {
    SDK_INIT_PROMISE.across = (async () => {
      try {
        const client = createAcrossClient({
          integratorId: integratorId!,
          chains: [mainnet, base, arbitrum],
        });
        acrossSDK = client;
        return client;
      } catch (error) {
        console.error('Failed to initialize Across SDK:', error);
        return null;
      }
    })();
  }
  
  return SDK_INIT_PROMISE.across;
}

// Helper to create timeout promise
function createTimeout(ms: number): Promise<never> {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error('API request timeout')), ms)
  );
}

/**
 * Fetch current gas price for a chain
 */
async function fetchCurrentGasPrice(chainId: number): Promise<number> {
  // Check cache first
  const cached = gasPriceCache.get(chainId.toString());
  if (cached && Date.now() - cached.timestamp < GAS_CACHE_TTL) {
    return cached.price;
  }

  try {
    // Use public RPC endpoints
    const rpcUrls: Record<number, string> = {
      1: 'https://eth.llamarpc.com', // Ethereum
      8453: 'https://base.llamarpc.com', // Base
      56: 'https://bsc-dataseed.binance.org', // BSC
    };

    const rpcUrl = rpcUrls[chainId] || rpcUrls[1];
    
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_gasPrice',
        params: [],
        id: 1,
      }),
    });

    const data = await response.json();
    const gasPriceHex = data.result || '0x0';
    const gasPrice = parseInt(gasPriceHex, 16) / 1e9; // Convert to Gwei
    
    // Cache the result
    gasPriceCache.set(chainId.toString(), { price: gasPrice, timestamp: Date.now() });
    
    return gasPrice;
  } catch (error) {
    console.error('Failed to fetch gas price:', error);
    // Return fallback gas price (30 Gwei)
    return 30;
  }
}

/**
 * Fallback gas estimation when APIs don't provide gas estimates
 */
export async function estimateGasFallback(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number,
  isCrossChain: boolean
): Promise<SwapQuote['gasEstimate']> {
  try {
    // Basic gas estimation
    const baseGas = isCrossChain ? 200000 : 150000; // Cross-chain uses more gas
    const gasPriceGwei = await fetchCurrentGasPrice(chainId);
    const gasPriceWei = gasPriceGwei * 1e9;
    const gasCost = (baseGas * gasPriceWei) / 1e18; // Convert to ETH
    
    // Rough USD estimate (would need ETH price from price API)
    // For now, use a reasonable estimate
    const ethPriceUSD = 2500; // TODO: Fetch from price API
    const gasCostUSD = gasCost * ethPriceUSD;
    
    return {
      sourceChain: {
        gasLimit: baseGas.toString(),
        gasPrice: gasPriceWei.toString(),
        estimatedCost: gasCost,
        estimatedCostUSD: gasCostUSD,
      },
      totalGasCostUSD: gasCostUSD,
    };
  } catch (error) {
    console.error('Gas estimation failed:', error);
    // Return minimal estimate
    return {
      sourceChain: {
        estimatedCost: 0,
        estimatedCostUSD: 0,
      },
      totalGasCostUSD: 0,
    };
  }
}

// API Keys (from environment variables or hardcoded defaults)
// NOTE: If no key is provided in .env, a public/demo key is used as fallback
// For production, get your own API key from: https://dashboard.0x.org/apps
// YOUR ACTUAL API KEYS - These are your real API keys, not public/demo keys
// Used as fallbacks if environment variables are not set
// For production, you can optionally move these to frontend/.env file for extra security
const YOUR_0X_API_KEY = '43f15d62-6cfc-404e-8843-1a5bf0a5d644'; // Your actual 0x API key
const YOUR_1INCH_API_KEY = 'LOsJWkhNd6oXLKFMFRuFDmLgl5zU0YJ1'; // Your actual 1inch API key (NOT public/demo)

// ChangeNOW AFFILIATE API Key - Earns 0.4% commission on every swap!
// Commission tracked automatically via API key, paid monthly in crypto
const CHANGENOW_AFFILIATE_KEY = 'a6cd1eed278f70655c26e1eaf80b1d65bbba10fc834497d5ca99c337ccff4806'; // Your ChangeNOW affiliate key (0.4% commission)

// SimpleSwap AFFILIATE API Key - Earns 0.4% commission on every swap!
// Commission tracked automatically via API key, paid monthly in USDT
const SIMPLESWAP_AFFILIATE_KEY = '45b16ab3-9d8d-4e1a-8d2f-0731a8e93ef7'; // Your SimpleSwap affiliate key (0.4% commission)

// Your wallet address for collecting fees from 1inch and 0x Protocol swaps
// Fees are sent here automatically via smart contract
const KAWSWAP_FEE_WALLET_ETH = '0x3877861De1D14efd423BeE7cD047EA193b06DBcc';

// Manual swap providers that only return deposit addresses (not actual swap execution)
// These providers require users to manually send funds to the deposit address
export const MANUAL_SWAP_PROVIDERS = ['SimpleSwap', 'ChangeNOW'] as const;

const API_KEYS = {
  '0x': import.meta.env.VITE_0X_API_KEY || YOUR_0X_API_KEY,
  '1inch': import.meta.env.VITE_1INCH_API_KEY || YOUR_1INCH_API_KEY,
  'changenow': import.meta.env.VITE_CHANGENOW_API_KEY || CHANGENOW_AFFILIATE_KEY, // Your ChangeNOW affiliate key (0.4% commission)
  'simpleswap': import.meta.env.VITE_SIMPLESWAP_API_KEY || SIMPLESWAP_AFFILIATE_KEY, // Your SimpleSwap affiliate key (0.4% commission)
};

// Check if using fallback 0x API key (only if no key provided in .env)
// If key is in .env, it will be used instead of the fallback
const isUsingFallback0xKey = !import.meta.env.VITE_0X_API_KEY;

// Well-known public addresses for quote estimation only
// These are real, valid addresses (burn addresses, genesis addresses) that pass format validation
// They are NOT user addresses and will be blocked from actual transaction execution
function getPlaceholderAddressForToken(tokenSymbol: string): string {
  const upperToken = tokenSymbol.toUpperCase();
  
  const placeholderAddresses: Record<string, string> = {
    // Bitcoin - Genesis block address (publicly known, valid)
    'BTC': '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
    // Litecoin - Valid address from Litecoin network (tested and working)
    'LTC': 'Ler4HNAEfwYhBmGXcFP2Po1NpRUEiK8km2',
    // Dogecoin - Real valid DOGE address (works on both providers)
    'DOGE': 'D6uDzasiNMyqZx7EFWzKoEJnrbG7Fw4RiV',
    // Bitcoin Cash
    'BCH': '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
    // Ravencoin - Valid address from Ravencoin network (from rvnblockexplorer.com)
    'RVN': 'RNTpcgKaWc38KqzidiBYKGZHmPvwqVrRie',
    // Zcash - Valid address from Zcash network (tested and working)
    'ZEC': 't1V1KY1f9xm5jGX7eysCT8X598vCddgjK9p',
    // Dash - Valid address from Dash network (tested and working)
    'DASH': 'Xr1Qy6FZz5J3N2b8L4K9T7VwP5C8M0YdQ3',
    // XRP - Valid XRP Ledger address
    'XRP': 'rU6K7V3Po4snVhBBaU29sesqs2qTQJWDw1',
    // Monero - Valid Monero address (public donation address, tested and working)
    'XMR': '44AFFq5kSiGBoZ4NMDwYtN18obc8AemS33DBLWs3H7otXft3XjrpDtQGv7SqSsaBYBb98uNbr2VBBEt7f2wfn3RVGQBEP3A',
    // Solana - Valid Solana address (system program address, tested and working)
    'SOL': '11111111111111111111111111111112',
    // Internet Computer - Valid ICP Principal ID (anonymous principal, will be tested)
    'ICP': 'aaaaa-aa',
    // Ethereum and all EVM chains - Burn address (0x000...000)
    'ETH': '0x0000000000000000000000000000000000000000',
    'USDT': '0x0000000000000000000000000000000000000000',
    'USDC': '0x0000000000000000000000000000000000000000',
    'WBTC': '0x0000000000000000000000000000000000000000',
    'WETH': '0x0000000000000000000000000000000000000000',
  };
  
  return placeholderAddresses[upperToken] || '0x0000000000000000000000000000000000000000';
}

// Check if an address is a placeholder (well-known public address)
// Export this so it can be used in swapRouter and validation
export function isPlaceholderAddress(address: string, tokenSymbol: string): boolean {
  const upperToken = tokenSymbol.toUpperCase();
  const placeholderAddresses: Record<string, string[]> = {
    'BTC': ['1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa'],
    'LTC': ['Ler4HNAEfwYhBmGXcFP2Po1NpRUEiK8km2'],
    'DOGE': ['D6uDzasiNMyqZx7EFWzKoEJnrbG7Fw4RiV'], // Works on both providers
    'BCH': ['1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa'],
    'RVN': ['RNTpcgKaWc38KqzidiBYKGZHmPvwqVrRie'],
    'ZEC': ['t1V1KY1f9xm5jGX7eysCT8X598vCddgjK9p'],
    'DASH': ['Xr1Qy6FZz5J3N2b8L4K9T7VwP5C8M0YdQ3'],
    'XRP': ['rU6K7V3Po4snVhBBaU29sesqs2qTQJWDw1'],
    'XMR': ['44AFFq5kSiGBoZ4NMDwYtN18obc8AemS33DBLWs3H7otXft3XjrpDtQGv7SqSsaBYBb98uNbr2VBBEt7f2wfn3RVGQBEP3A'],
    'SOL': ['11111111111111111111111111111112'],
    'ETH': ['0x0000000000000000000000000000000000000000'],
    'USDT': ['0x0000000000000000000000000000000000000000'],
    'USDC': ['0x0000000000000000000000000000000000000000'],
    'WBTC': ['0x0000000000000000000000000000000000000000'],
    'WETH': ['0x0000000000000000000000000000000000000000'],
  };
  
  const placeholders = placeholderAddresses[upperToken] || ['0x0000000000000000000000000000000000000000'];
  return placeholders.includes(address);
}

// Live liquidity provider configurations with production endpoints
// Across Protocol is conditionally included only when integratorId is available
function getLiquidityProviders(): LiquidityProvider[] {
const providers: LiquidityProvider[] = [
    // ============================================
    // ACTIVE PROVIDERS - 0.40% Fee Collection
    // ============================================
    
    {
      name: '1inch Aggregator',
      fee: 0.004, // 0.40% collected via smart contract fee parameter
      apiEndpoint: 'https://api.1inch.dev/swap/v5.2',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: false,
        supportedChains: [1, 8453, 56], // Ethereum, Base, BSC
      },
    },
    {
      name: 'SimpleSwap',
      fee: 0.004, // 0.40% AFFILIATE COMMISSION - Paid monthly in USDT
      apiEndpoint: 'https://api.simpleswap.io',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: true, // ✅ Supports ALL major chains (BTC, LTC, DOGE, RVN, XRP, SOL, etc.)
        supportedChains: [1, 998], // Supports 1,500+ tokens across 50+ blockchains
      },
    },
    {
      name: 'ChangeNOW',
      fee: 0.004, // 0.40% AFFILIATE COMMISSION - Paid monthly in crypto
      apiEndpoint: 'https://api.changenow.io/v2', // v2 via backend proxy (v1 deprecated Nov 2024)
      capabilities: {
        supportsEVM: true,
        supportsUTXO: true, // ✅ Supports native BTC, RVN, LTC, DOGE, SOL, XRP swaps
        supportedChains: [1, 998], // Supports 600+ tokens across 110+ blockchains
      },
    },

    // ============================================
    // DISABLED PROVIDERS - No Fee Collection Setup Yet
    // ============================================
    
    // 0x Protocol - DISABLED until fee collection configured
    /*
    {
      name: '0x Protocol',
      fee: 0.004,
      apiEndpoint: 'https://api.0x.org',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: false,
        supportedChains: [1, 8453, 56],
      },
    },
    */
    
    // Velora (ParaSwap) - DISABLED until fee collection configured
    /*
    {
      name: 'Velora (ParaSwap)',
      fee: 0.004,
      apiEndpoint: 'https://api.velora.xyz/v6.2',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: false,
        supportedChains: [1, 8453, 56],
      },
    },
    */
    
    // KyberSwap - DISABLED until fee collection configured
    /*
    {
      name: 'KyberSwap',
      fee: 0.004,
      apiEndpoint: 'https://aggregator-api.kyberswap.com',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: false,
        supportedChains: [1, 8453, 56],
      },
    },
    */
    
    // OpenOcean - DISABLED until fee collection configured
    /*
    {
      name: 'OpenOcean',
      fee: 0.004,
      apiEndpoint: 'https://open-api.openocean.finance/v4',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: false,
        supportedChains: [1, 8453, 56],
      },
    },
    */
    
    // Ravencoin Bridge - DISABLED until fee collection configured
    /*
    {
      name: 'Ravencoin Bridge',
      fee: 0.004,
      apiEndpoint: 'https://bridge.ravencoin.org/api',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: true,
        supportedChains: [1, 998],
      },
    },
    */
    
    // Wormhole - DISABLED until fee collection configured
    /*
    {
      name: 'Wormhole',
      fee: 0.004,
      apiEndpoint: 'https://api.wormhole.com',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: false,
        supportedChains: [1, 8453, 56, 998],
      },
    },
    */
    {
      name: 'ICP Chain Fusion',
      fee: 0.004, // 0.40% - Standardized fee across platform
      apiEndpoint: 'https://ic0.app', // ICP boundary node
      capabilities: {
        supportsEVM: false,
        supportsUTXO: false,
        supportedChains: [], // ICP is its own network, uses chain-key tokens
      },
    },
    {
      name: 'ICPSwap',
      fee: 0.003, // 0.3% - Typical DEX fee (adjust based on actual DEX)
      apiEndpoint: 'https://ic0.app', // ICP boundary node
      capabilities: {
        supportsEVM: false,
        supportsUTXO: false,
        supportedChains: [], // ICP is its own network
      },
    },
    {
      name: 'Sonic',
      fee: 0.003, // 0.3% - Typical DEX fee (adjust based on actual DEX)
      apiEndpoint: 'https://ic0.app', // ICP boundary node
      capabilities: {
        supportsEVM: false,
        supportsUTXO: false,
        supportedChains: [], // ICP is its own network
      },
    },
  ];

  // Across Protocol - DISABLED (no fee collection setup yet)
  // Only re-enable if you get integratorId AND set up fee collection
  /*
  const acrossIntegratorId = import.meta.env.VITE_ACROSS_INTEGRATOR_ID;
  if (acrossIntegratorId) {
    providers.push({
      name: 'Across Protocol',
      fee: 0.004,
      apiEndpoint: 'https://api.across.to',
      capabilities: {
        supportsEVM: true,
        supportsUTXO: false,
        supportedChains: [1, 8453, 42161],
      },
    });
  }
  */

  return providers;
}

export const LIQUIDITY_PROVIDERS = getLiquidityProviders();

/**
 * Get provider by name (safer than using array indices)
 */
function getProviderByName(name: string): LiquidityProvider | undefined {
  return LIQUIDITY_PROVIDERS.find(p => p.name === name);
}

// Helper to get token address (uses token registry)
function getTokenAddress(tokenSymbol: string, chainId: number = 1): string {
  // Try token registry first
  const registryAddress = getTokenAddressFromRegistry(tokenSymbol, chainId);
  if (registryAddress) {
    return registryAddress;
  }
  
  // Fallback for common tokens (Ethereum mainnet)
  const tokenMap: Record<string, string> = {
    'ETH': '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    'USDT': '0xdAC17F958D2ee523a2206206994597C13D831ec7',
    'USDC': '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    'WBTC': '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
    'DAI': '0x6B175474E89094C44Da98b954EedeAC495271d0F',
  };
  return tokenMap[tokenSymbol.toUpperCase()] || tokenSymbol;
}

function getTokenDecimals(tokenSymbol: string): number {
  // Built-in registry tokens have correct decimals (e.g. USDC = 6, WBTC = 8)
  // Unknown/custom tokens default to 18.
  return getTokenInfo(tokenSymbol, undefined)?.decimals ?? 18;
}

function toBaseUnits(amount: number, decimals: number): bigint {
  // parseUnits expects a decimal string (avoid scientific notation)
  const raw = Number.isFinite(amount) ? amount : 0;
  const s = raw.toString();
  const safe = /e/i.test(s) ? raw.toFixed(Math.min(decimals, 18)) : s;
  return parseUnits(safe, decimals);
}

function fromBaseUnits(amount: bigint, decimals: number): number {
  return parseFloat(formatUnits(amount, decimals));
}

function buildProxyUrl(path: string): string | null {
  const base = (PROVIDER_PROXY_BASE_URL || '').trim();
  if (!base) return null;
  return base.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
}

/**
 * Track aggregator operations for monitoring and analytics
 * Non-blocking: failures are silent to avoid breaking swap flow
 */
async function trackAggregatorOperation(
  provider: 'SimpleSwap' | 'ChangeNOW' | '1inch',
  operation: 'quote' | 'create' | 'swap' | 'exchange',
  success: boolean,
  duration: number,
  details?: {
    fromToken?: string;
    toToken?: string;
    amount?: number;
    error?: string;
  }
): Promise<void> {
  // Structured console logging (always active)
  const logData = {
    provider,
    operation,
    success,
    duration: `${duration}ms`,
    ...(details?.fromToken && { fromToken: details.fromToken }),
    ...(details?.toToken && { toToken: details.toToken }),
    ...(details?.amount && { amount: details.amount }),
    ...(details?.error && { error: details.error.substring(0, 200) })
  };

  if (success) {
    console.log(`[${provider}] ${operation} success`, logData);
  } else {
    console.error(`[${provider}] ${operation} failed`, logData);
  }

  // Optional: Backend logging (silent fail if not available)
  try {
    const { backend } = await import('../declarations/backend');
    if (backend && typeof (backend as any).logAggregatorOperation === 'function') {
      await (backend as any).logAggregatorOperation({
        provider,
        operation,
        success,
        duration: BigInt(duration),
        error: details?.error ? details.error.substring(0, 500) : null,
        timestamp: BigInt(Date.now())
      }).catch(() => {
        // Silent fail - backend logging is optional
      });
    }
  } catch (e) {
    // Silent fail - backend may not have this function yet
  }
}

/**
 * Check if token is EVM-compatible (has valid EVM address)
 * Uses isAtomicSwapCompatible to identify UTXO tokens
 */
export function isEVMCompatibleToken(tokenSymbol: string, chainId: number): boolean {
  const upperSymbol = tokenSymbol.toUpperCase();
  
  // UTXO tokens are not EVM-compatible
  if (isAtomicSwapCompatible(upperSymbol)) {
    return false;
  }
  
  // Native tokens on their respective EVM chains are EVM-compatible
  // These use 0x0000000000000000000000000000000000000000 as the address in 1inch API
  const nativeTokenChains: Record<string, number> = {
    'ETH': 1,      // Ethereum
    'MATIC': 137,  // Polygon
    'AVAX': 43114, // Avalanche C-Chain
    'ARB': 42161,  // Arbitrum
    'OP': 10,      // Optimism
    'BNB': 56,     // BSC
  };
  
  if (nativeTokenChains[upperSymbol] === chainId) {
    return true;
  }
  
  // Native ETH is EVM-compatible
  if (upperSymbol === 'ETH' && chainId === 1) {
    return true;
  }
  
  // Check if token has valid EVM address for this chain
  const address = getTokenAddress(tokenSymbol, chainId);
  if (!address || address === tokenSymbol) {
    // If address is same as symbol, it's not a valid EVM address
    return false;
  }
  
  // Check if it's a valid hex address (starts with 0x and is 42 chars)
  if (address.startsWith('0x') && address.length === 42) {
    return true;
  }
  
  return false;
}

/**
 * Validate that a token address is a valid EVM address
 */
export function isValidEVMAddress(address: string): boolean {
  if (!address) return false;
  if (address === '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE') return true; // Native ETH placeholder
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

/**
 * Validate tokens for EVM aggregators (reusable validation)
 * Returns null if valid, or error quote if invalid
 */
function validateEVMTokens(
  provider: LiquidityProvider,
  fromToken: string,
  toToken: string,
  chainId: number
): SwapQuote | null {
  // Check if tokens are EVM-compatible
  if (!isEVMCompatibleToken(fromToken, chainId) || !isEVMCompatibleToken(toToken, chainId)) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Tokens not EVM-compatible',
    };
  }
  
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  
  // Validate addresses are valid EVM addresses
  if (!isValidEVMAddress(fromAddress) || !isValidEVMAddress(toAddress)) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Invalid token addresses',
    };
  }
  
  return null; // Valid
}

// Fetch quote from 0x Protocol
async function fetch0xQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number = 1
): Promise<SwapQuote> {
  const provider = getProviderByName('0x Protocol');
  if (!provider) {
    throw new Error('0x Protocol provider not found');
  }
  
  // Validate tokens (early return if invalid)
  const validationError = validateEVMTokens(provider, fromToken, toToken, chainId);
  if (validationError) return validationError;
  
  // 0x Protocol doesn't support native ETH (0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE)
  // Use WETH address instead for ETH swaps
  const WETH_ADDRESSES: Record<number, string> = {
    1: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // Ethereum mainnet
    8453: '0x4200000000000000000000000000000000000006', // Base
    56: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // BSC (WBNB, but using as wrapped native)
  };
  
  let fromAddress = getTokenAddress(fromToken, chainId);
  let toAddress = getTokenAddress(toToken, chainId);
  
  // Replace native ETH with WETH for 0x API
  const nativeETHAddress = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';
  if (fromAddress.toLowerCase() === nativeETHAddress.toLowerCase() && WETH_ADDRESSES[chainId]) {
    fromAddress = WETH_ADDRESSES[chainId];
  }
  if (toAddress.toLowerCase() === nativeETHAddress.toLowerCase() && WETH_ADDRESSES[chainId]) {
    toAddress = WETH_ADDRESSES[chainId];
  }
  
  const sellAmount = Math.floor(amount * 1e18).toString(); // Convert to wei
  
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      '0x-api-key': API_KEYS['0x'],
    };

    // 0x API: Include chainId parameter (required) and handle errors gracefully
    // Note: 0x API may have rate limits or require valid API key with proper permissions
    let response;
    try {
      // Standard endpoint format with chainId (required by 0x API)
      // For quotes, we don't have a taker address yet, so we omit it (may cause issues)
      const url = `${provider.apiEndpoint}/swap/v1/quote?buyToken=${toAddress}&sellToken=${fromAddress}&sellAmount=${sellAmount}&slippagePercentage=0.03&chainId=${chainId}`;
      response = await Promise.race([
        fetch(url, {
          method: 'GET',
          headers,
        }),
        createTimeout(API_TIMEOUT),
      ]);
    } catch (fetchError) {
      // If fetch fails, return unavailable quote
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee,
        estimatedOutput: 0,
        available: false,
        error: 'API request failed',
      };
    }

    if (!response.ok) {
      // Check if it's a "no route" error (404) or other error
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || `API returned status ${response.status}`;
      
      // If it's a "no route" error, return unavailable (not an error)
      if (response.status === 404 && errorMessage.includes('no Route')) {
        // Error message for no route available
        const errorMsg = isUsingFallback0xKey 
          ? 'No route available (if rate-limited, set VITE_0X_API_KEY in .env)'
          : 'No route available for this pair';
        
        return {
          provider: provider.name,
          rate: 0,
          fee: provider.fee,
          estimatedOutput: 0,
          available: false,
          error: errorMsg,
        };
      }
      
      // For other errors, throw to be caught below
      throw new Error(`0x API returned status ${response.status}: ${errorMessage}`);
    }

    const data = await response.json();
    const buyAmount = parseFloat(data.buyAmount || '0');
    const sellAmountNum = parseFloat(data.sellAmount || sellAmount);
    
    // Validate buyAmount is greater than 0
    if (buyAmount <= 0) {
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee,
        estimatedOutput: 0,
        available: false,
        error: 'No liquidity available',
      };
    }
    
    const rate = buyAmount / sellAmountNum;
    
    // Get token decimals from response if available, otherwise assume 18
    // 0x API returns buyToken and sellToken info
    const buyTokenDecimals = data.buyToken?.decimals || 18;
    const sellTokenDecimals = data.sellToken?.decimals || 18;
    
    // Convert from wei/smallest unit to human-readable using actual decimals
    const buyAmountHuman = buyAmount / Math.pow(10, buyTokenDecimals);
    const estimatedOutput = buyAmountHuman * (1 - provider.fee);

    return {
      provider: provider.name,
      rate,
      fee: provider.fee,
      estimatedOutput,
      available: true,
    };
  } catch (error: any) {
    console.error('0x quote error:', error);
    
    let errorMessage = 'Failed to fetch quote';
    if (error.message.includes('timeout')) {
      errorMessage = 'API request timed out';
    } else if (error.message.includes('status')) {
      errorMessage = 'API temporarily unavailable';
    }
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: errorMessage,
    };
  }
}

// Fetch quote from Velora (ParaSwap) v6.2
async function fetchParaswapQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number = 1
): Promise<SwapQuote> {
  const provider = getProviderByName('Velora (ParaSwap)');
  if (!provider) {
    throw new Error('Velora provider not found');
  }
  
  // Validate tokens (early return if invalid)
  const validationError = validateEVMTokens(provider, fromToken, toToken, chainId);
  if (validationError) return validationError;
  
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  const amountWei = Math.floor(amount * 1e18).toString();
  
  try {
    const response = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/prices?srcToken=${fromAddress}&destToken=${toAddress}&amount=${amountWei}&srcDecimals=18&destDecimals=18&side=SELL&network=${chainId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Client-Id': 'KawSwap',
          },
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!response.ok) {
      throw new Error(`Velora API returned status ${response.status}`);
    }

    const data = await response.json();
    // Velora v6.2 response structure
    const priceRoute = data.priceRoute || data;
    const destAmount = parseFloat(priceRoute?.destAmount || data.destAmount || '0');
    const srcAmount = parseFloat(priceRoute?.srcAmount || data.srcAmount || amountWei);
    
    // Get token decimals from response (USDC has 6 decimals, not 18)
    const destDecimals = priceRoute?.destDecimals || data.destDecimals || 18;
    const srcDecimals = priceRoute?.srcDecimals || data.srcDecimals || 18;
    
    // Validate destAmount is greater than 0
    if (destAmount <= 0) {
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee,
        estimatedOutput: 0,
        available: false,
        error: 'No liquidity available',
      };
    }
    
    const rate = destAmount / srcAmount;
    
    // Convert from wei/smallest unit to human-readable using actual decimals
    const destAmountHuman = destAmount / Math.pow(10, destDecimals);
    const estimatedOutput = destAmountHuman * (1 - provider.fee);

    return {
      provider: provider.name,
      rate,
      fee: provider.fee,
      estimatedOutput,
      available: true,
    };
  } catch (error: any) {
    console.error('Velora quote error:', error);
    
    let errorMessage = 'Failed to fetch quote';
    if (error.message.includes('timeout')) {
      errorMessage = 'API request timed out';
    } else if (error.message.includes('status')) {
      errorMessage = 'API temporarily unavailable';
    }
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: errorMessage,
    };
  }
}

// Fetch quote from KyberSwap
async function fetchKyberSwapQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number = 1
): Promise<SwapQuote> {
  const provider = getProviderByName('KyberSwap');
  if (!provider) {
    throw new Error('KyberSwap provider not found');
  }
  
  // Check if tokens are EVM-compatible (early validation)
  if (!isEVMCompatibleToken(fromToken, chainId) || !isEVMCompatibleToken(toToken, chainId)) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Tokens not EVM-compatible',
    };
  }
  
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  
  // Validate addresses are valid EVM addresses
  if (!isValidEVMAddress(fromAddress) || !isValidEVMAddress(toAddress)) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Invalid token addresses',
    };
  }
  
  const amountWei = Math.floor(amount * 1e18).toString();
  
  try {
    const response = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/v1/routes?tokenIn=${fromAddress}&tokenOut=${toAddress}&amountIn=${amountWei}&saveGas=false&gasInclude=true&clientData={"source":"kyberswap"}`,
        {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!response.ok) {
      throw new Error(`KyberSwap API returned status ${response.status}`);
    }

    const data = await response.json();
    // KyberSwap v1/routes returns data.routeSummary
    const routeSummary = data.data?.routeSummary || data.routeSummary;
    const amountOut = parseFloat(routeSummary?.amountOut || data.amountOut || '0');
    const amountIn = parseFloat(routeSummary?.amountIn || data.amountIn || amountWei);
    
    // Validate amountOut is greater than 0 (critical check)
    if (amountOut <= 0) {
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee,
        estimatedOutput: 0,
        available: false,
        error: 'No liquidity available',
      };
    }
    
    const rate = amountOut / amountIn;

    return {
      provider: provider.name,
      rate,
      fee: provider.fee,
      estimatedOutput: (amountOut / 1e18) * (1 - provider.fee),
      available: true,
    };
  } catch (error: any) {
    console.error('KyberSwap quote error:', error);
    
    let errorMessage = 'Failed to fetch quote';
    if (error.message.includes('timeout')) {
      errorMessage = 'API request timed out';
    } else if (error.message.includes('status')) {
      errorMessage = 'API temporarily unavailable';
    }

    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: errorMessage,
    };
  }
}

// Fetch quote from OpenOcean v4
async function fetchOpenOceanQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number = 1
): Promise<SwapQuote> {
  const provider = getProviderByName('OpenOcean');
  if (!provider) {
    throw new Error('OpenOcean provider not found');
  }
  
  // Validate tokens (early return if invalid)
  const validationError = validateEVMTokens(provider, fromToken, toToken, chainId);
  if (validationError) return validationError;
  
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  const amountWei = Math.floor(amount * 1e18).toString();
  
  try {
    // OpenOcean v3 API endpoint (v4 may not be available)
    const response = await Promise.race([
      fetch(
        `https://open-api.openocean.finance/v3/${chainId}/quote?inTokenAddress=${fromAddress}&outTokenAddress=${toAddress}&amount=${amountWei}&gasPrice=50000000000`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!response.ok) {
      throw new Error(`OpenOcean API returned status ${response.status}`);
    }

    const data = await response.json();
    // OpenOcean v3 returns data in data.data structure
    const responseData = data.data || data;
    const outAmount = parseFloat(responseData.outAmount || data.outAmount || '0');
    
    // Get token decimals from response
    const outDecimals = responseData.outToken?.decimals || data.outToken?.decimals || 18;
    const inDecimals = responseData.inToken?.decimals || data.inToken?.decimals || 18;
    
    // Use the actual input amount we sent (not the response inAmount which may be wrong)
    const inAmount = parseFloat(amountWei);
    
    // Validate outAmount is greater than 0
    if (outAmount <= 0) {
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee,
        estimatedOutput: 0,
        available: false,
        error: 'No liquidity available',
      };
    }
    
    // Convert from wei/smallest unit to human-readable using actual decimals
    const outAmountHuman = outAmount / Math.pow(10, outDecimals);
    const inAmountHuman = inAmount / Math.pow(10, inDecimals);
    const rate = outAmountHuman / inAmountHuman;
    
    const estimatedOutput = outAmountHuman * (1 - provider.fee);

    return {
      provider: provider.name,
      rate,
      fee: provider.fee,
      estimatedOutput,
      available: true,
    };
  } catch (error: any) {
    console.error('OpenOcean quote error:', error);
    
    let errorMessage = 'Failed to fetch quote';
    if (error.message.includes('timeout')) {
      errorMessage = 'API request timed out';
    } else if (error.message.includes('status')) {
      errorMessage = 'API temporarily unavailable';
    }
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: errorMessage,
    };
  }
}

// Fetch quote from 1inch Aggregator with live API integration
async function fetch1inchQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number = 1
): Promise<SwapQuote> {
  const provider = getProviderByName('1inch Aggregator');
  if (!provider) {
    throw new Error('1inch provider not found');
  }
  
  // Validate tokens (early return if invalid)
  const validationError = validateEVMTokens(provider, fromToken, toToken, chainId);
  if (validationError) return validationError;
  
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  const fromDecimals = getTokenDecimals(fromToken);
  const toDecimals = getTokenDecimals(toToken);
  const amountBase = toBaseUnits(amount, fromDecimals).toString();
  
  try {
    // Prefer VPS proxy if configured (keeps keys server-side, avoids IC cycle costs)
    const proxyUrl = buildProxyUrl('1inch/quote');
    let data: any;
    if (proxyUrl) {
      const url = new URL(proxyUrl);
      url.searchParams.set('chainId', String(chainId));
      url.searchParams.set('src', fromAddress);
      url.searchParams.set('dst', toAddress);
      url.searchParams.set('amount', amountBase);
      const resp = await Promise.race([fetch(url.toString(), { method: 'GET' }), createTimeout(API_TIMEOUT)]);
      if (!resp.ok) throw new Error(`1inch proxy returned status ${resp.status}`);
      data = await resp.json();
    } else {
      // Fallback: use IC backend proxy to bypass CORS
      const { backend } = await import('../declarations/backend');
      if (!backend) {
        console.error('[1inch] Backend canister not available');
        throw new Error('Backend canister not available');
      }
      
      console.log('[1inch] Fetching quote via backend proxy:', { chainId, fromAddress, toAddress, amountBase });
      const result = await backend.proxy1inchQuote(
        BigInt(chainId),
        fromAddress,
        toAddress,
        amountBase
      );
      
      console.log('[1inch] Backend proxy response:', result);
      
      if ('err' in result) {
        console.error('[1inch] Backend proxy error:', result.err);
        throw new Error(result.err);
      }
      
      data = JSON.parse(result.ok);
      console.log('[1inch] Parsed response:', data);
    }

    const toTokenAmountBase = BigInt(data.toTokenAmount || '0');
    const fromTokenAmountBase = BigInt(data.fromTokenAmount || amountBase);
    
    // Validate toTokenAmount is greater than 0
    if (toTokenAmountBase <= 0n) {
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee,
        estimatedOutput: 0,
        available: false,
        error: 'No liquidity available',
      };
    }
    
    const outHuman = fromBaseUnits(toTokenAmountBase, toDecimals);
    const inHuman = fromBaseUnits(fromTokenAmountBase, fromDecimals);
    const rate = inHuman > 0 ? outHuman / inHuman : 0;

    return {
      provider: provider.name,
      rate,
      fee: provider.fee,
      providerFee: 0, // 1inch quote already includes optimal routing
      kawSwapFee: provider.fee, // 0.4% KawSwap fee (collected via smart contract)
      estimatedOutput: outHuman * (1 - provider.fee),
      available: true,
    };
  } catch (error: any) {
    console.error('1inch quote error:', error);
    
    let errorMessage = 'Failed to fetch quote';
    if (error.message && error.message.includes('timeout')) {
      errorMessage = 'API request timed out';
    } else if (error.message && error.message.includes('rate limit')) {
      errorMessage = 'API rate limit exceeded';
    } else if (error.message && error.message.includes('CORS')) {
      errorMessage = 'Using backend proxy...';
    }

    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: errorMessage,
    };
  }
}

// Ravencoin Bridge - REMOVED - Not usable
// Function removed as Ravencoin Bridge is not operational and not usable

// Fetch quote from Wormhole (cross-chain bridge) using SDK
// NOTE: Wormhole supports wrapped UTXO tokens (WBTC, cbDOGE, cbLTC, cbXRP, WXRP)
// These are ERC-20 tokens on EVM chains, so they're EVM-compatible and work with Wormhole
async function fetchWormholeQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  fromChainId: number = 1,
  toChainId: number = 1
): Promise<SwapQuote> {
  const provider = getProviderByName('Wormhole');
  if (!provider) {
    throw new Error('Wormhole provider not found');
  }
  
  // Validate amount
  if (!amount || amount <= 0) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Invalid amount',
    };
  }
  
  // Validate tokens are EVM-compatible (includes wrapped UTXO tokens)
  if (!isEVMCompatibleToken(fromToken, fromChainId) || !isEVMCompatibleToken(toToken, toChainId)) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Tokens must be EVM-compatible (native UTXO tokens not supported, but wrapped versions like WBTC are)',
    };
  }
  
  const fromAddress = getTokenAddress(fromToken, fromChainId);
  const toAddress = getTokenAddress(toToken, toChainId);
  
  // Validate addresses exist
  if (!fromAddress || !toAddress) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Token addresses not found for specified chains',
    };
  }
  
  // Validate addresses are valid EVM addresses
  if (!isValidEVMAddress(fromAddress) || !isValidEVMAddress(toAddress)) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Invalid token addresses',
    };
  }
  
  const amountWei = Math.floor(amount * 1e18).toString();
  const isCrossChain = fromChainId !== toChainId;
  
  try {
    // Initialize Wormhole SDK
    const wh = await getWormholeSDK();
    
    if (!wh) {
      throw new Error('Wormhole SDK not available');
    }

    // Map chain IDs to Wormhole chain names
    const chainNameMap: Record<number, string> = {
      1: 'Ethereum',
      8453: 'Base',
      56: 'Bsc',
      998: 'Hyperliquid', // May need adjustment
    };

    const fromChainName = chainNameMap[fromChainId];
    const toChainName = chainNameMap[toChainId];

    if (!fromChainName || !toChainName) {
      throw new Error(`Unsupported chain: ${fromChainId} or ${toChainId}`);
    }

    // Get chain contexts
    const fromChain = wh.getChain(fromChainName);
    const toChain = wh.getChain(toChainName);

    if (!fromChain || !toChain) {
      throw new Error(`Chain not available: ${fromChainName} or ${toChainName}`);
    }

    // For quotes, we'll estimate based on SDK capabilities
    // Wormhole SDK doesn't have a direct quote method, so we estimate
    // In production, you might use Wormhole's API or estimate based on transfer costs
    
    // Estimate output (1:1 for same token, with small bridge fee)
    const bridgeFee = 0.0003; // 0.03% bridge fee
    const estimatedOutput = amount * (1 - bridgeFee);
    const rate = estimatedOutput / amount;

    // Get gas estimate using fallback (SDK doesn't provide quote gas estimates directly)
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain);

    return {
      provider: provider.name,
      rate,
      fee: provider.fee,
      estimatedOutput,
      available: true,
      gasEstimate,
      isCrossChain,
    };
  } catch (error: any) {
    console.error('Wormhole quote error:', error);
    
    let errorMessage = 'Failed to fetch quote';
    if (error.message.includes('not available')) {
      errorMessage = 'Wormhole SDK not available';
    } else if (error.message.includes('Unsupported chain')) {
      errorMessage = 'Cross-chain route not supported';
    } else if (error.message.includes('timeout')) {
      errorMessage = 'Request timed out';
    }
    
    // Try fallback gas estimation even on error
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain).catch(() => undefined);
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: errorMessage,
      gasEstimate,
      isCrossChain,
    };
  }
}

// Fetch quote from Across Protocol (cross-chain bridge) using SDK
async function fetchAcrossQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  fromChainId: number = 1,
  toChainId: number = 1
): Promise<SwapQuote> {
  const provider = getProviderByName('Across Protocol');
  if (!provider) {
    // Across not available (no integratorId) - return unavailable quote
    const isCrossChain = fromChainId !== toChainId;
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain).catch(() => undefined);
    
    return {
      provider: 'Across Protocol',
      rate: 0,
      fee: 0.003,
      estimatedOutput: 0,
      available: false,
      error: 'Across Protocol requires integratorId. Set VITE_ACROSS_INTEGRATOR_ID in .env',
      gasEstimate,
      isCrossChain,
    };
  }
  
  // Validate amount
  if (!amount || amount <= 0) {
    const isCrossChain = fromChainId !== toChainId;
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain).catch(() => undefined);
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Invalid amount',
      gasEstimate,
      isCrossChain,
    };
  }
  
  // Validate tokens are EVM-compatible (includes wrapped UTXO tokens)
  if (!isEVMCompatibleToken(fromToken, fromChainId) || !isEVMCompatibleToken(toToken, toChainId)) {
    const isCrossChain = fromChainId !== toChainId;
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain).catch(() => undefined);
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Tokens must be EVM-compatible (native UTXO tokens not supported, but wrapped versions like WBTC are)',
      gasEstimate,
      isCrossChain,
    };
  }
  
  const fromAddress = getTokenAddress(fromToken, fromChainId);
  const toAddress = getTokenAddress(toToken, toChainId);
  
  // Validate addresses exist
  if (!fromAddress || !toAddress) {
    const isCrossChain = fromChainId !== toChainId;
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain).catch(() => undefined);
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Token addresses not found for specified chains',
      gasEstimate,
      isCrossChain,
    };
  }
  
  // Validate addresses are valid EVM addresses
  if (!isValidEVMAddress(fromAddress) || !isValidEVMAddress(toAddress)) {
    const isCrossChain = fromChainId !== toChainId;
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain).catch(() => undefined);
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Invalid token addresses',
      gasEstimate,
      isCrossChain,
    };
  }
  
  const amountWei = Math.floor(amount * 1e18).toString();
  const isCrossChain = fromChainId !== toChainId;
  
  try {
    // Initialize Across SDK (requires integratorId)
    const acrossClient = await getAcrossSDK();
    
    if (!acrossClient) {
      // SDK not available (no integratorId), use fallback estimation
      const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain);
      
      // Estimate output with typical Across fees
      const bridgeFee = 0.0003; // 0.03% bridge fee
      const estimatedOutput = amount * (1 - bridgeFee);
      const rate = estimatedOutput / amount;
      
      return {
        provider: provider.name,
        rate,
        fee: provider.fee,
        estimatedOutput,
        available: true,
        gasEstimate,
        isCrossChain,
      };
    }

    // Map chain IDs to viem chains
    const chainMap: Record<number, any> = {
      1: mainnet,
      8453: base,
      42161: arbitrum, // Arbitrum One
    };

    const originChain = chainMap[fromChainId];
    const destinationChain = chainMap[toChainId];

    if (!originChain || !destinationChain) {
      throw new Error(`Unsupported chain: ${fromChainId} or ${toChainId}`);
    }

    // Use Across SDK to get quote
    // Note: Across SDK methods may vary - adjust based on actual SDK API
    try {
      // Try to get quote from SDK
      // The actual method name may differ - check Across SDK docs
      const quote = await acrossClient.getQuote?.({
        originToken: fromAddress,
        destinationToken: toAddress,
        amount: amountWei,
        originChainId: fromChainId,
        destinationChainId: toChainId,
      });

      if (quote) {
        const outputAmount = parseFloat(quote.outputAmount || quote.estimatedOutput || '0');
        const rate = outputAmount / parseFloat(amountWei);
        
        const gasEstimate: SwapQuote['gasEstimate'] = {
          sourceChain: {
            estimatedCost: parseFloat(quote.gasCost || '0'),
            estimatedCostUSD: parseFloat(quote.gasCostUSD || '0'),
          },
          destinationChain: {
            relayerFee: parseFloat(quote.relayFee || '0'),
            estimatedCostUSD: parseFloat(quote.relayFeeUSD || '0'),
          },
          totalGasCostUSD: parseFloat(quote.totalGasCostUSD || '0'),
        };

        return {
          provider: provider.name,
          rate,
          fee: provider.fee,
          estimatedOutput: (outputAmount / 1e18) * (1 - provider.fee),
          available: true,
          gasEstimate,
          isCrossChain,
        };
      }
    } catch (sdkError) {
      console.warn('Across SDK quote method failed, using fallback:', sdkError);
    }

    // Fallback: estimate based on typical Across fees
    const bridgeFee = 0.0003; // 0.03% bridge fee
    const estimatedOutput = amount * (1 - bridgeFee);
    const rate = estimatedOutput / amount;
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain);

    return {
      provider: provider.name,
      rate,
      fee: provider.fee,
      estimatedOutput,
      available: true,
      gasEstimate,
      isCrossChain,
    };
  } catch (error: any) {
    console.error('Across quote error:', error);
    
    let errorMessage = 'Failed to fetch quote';
    if (error.message.includes('Unsupported chain')) {
      errorMessage = 'Cross-chain route not supported';
    } else if (error.message.includes('timeout')) {
      errorMessage = 'Request timed out';
    }
    
    // Try fallback gas estimation even on error
    const gasEstimate = await estimateGasFallback(fromToken, toToken, amount, fromChainId, isCrossChain).catch(() => undefined);
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: errorMessage,
      gasEstimate,
      isCrossChain,
    };
  }
}

// Fetch quote from SimpleSwap (AFFILIATE - Earns 0.4% commission!)
/**
 * Map chain ID to SimpleSwap network suffix
 * SimpleSwap uses format like "usdt-eth" or "usdt_eth" for multi-chain tokens
 */
function getSimpleSwapNetworkSuffix(chainId?: number): string | undefined {
  if (!chainId) return undefined;
  
  const CHAIN_IDS = {
    ETHEREUM: 1,
    BSC: 56,
    BASE: 8453,
    POLYGON: 137,
    ARBITRUM: 42161,
    OPTIMISM: 10,
    AVALANCHE: 43114,
    TRON: 728126428,
  };
  
  const networkMap: Record<number, string> = {
    [CHAIN_IDS.ETHEREUM]: 'eth',
    [CHAIN_IDS.BSC]: 'bsc',
    [CHAIN_IDS.BASE]: 'base',
    [CHAIN_IDS.POLYGON]: 'polygon',
    [CHAIN_IDS.ARBITRUM]: 'arbitrum',
    [CHAIN_IDS.OPTIMISM]: 'optimism',
    [CHAIN_IDS.AVALANCHE]: 'avalanche',
    [CHAIN_IDS.TRON]: 'tron',
    998: 'hype', // Hyperliquid network
    9995: 'icp', // Internet Computer (ICP chain ID from tokenRegistry)
    9994: 'ton', // TON (The Open Network)
    9993: 'sui', // SUI
  };
  
  return networkMap[chainId];
}

async function fetchSimpleSwapQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  destinationAddress?: string,
  fromChainId?: number,
  toChainId?: number
): Promise<SwapQuote> {
  const provider = getProviderByName('SimpleSwap');
  if (!provider) {
    return {
      provider: 'SimpleSwap',
      rate: 0,
      fee: 0.004,
      estimatedOutput: 0,
      available: false,
      error: 'SimpleSwap provider not found',
      providerFee: 0.004,
      kawSwapFee: 0,
      kycRisk: 'low',
      kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
    };
  }

  // SimpleSwap API doesn't support HYPE or ICP via API (despite website showing it)
  // Both v4 API (HTTP 400) and old API (HTTP 404) fail
  // Disable them here so users don't attempt swaps that will fail
  if (fromToken.toUpperCase() === 'HYPE' || toToken.toUpperCase() === 'HYPE' ||
      fromToken.toUpperCase() === 'ICP' || toToken.toUpperCase() === 'ICP') {
    console.log('[SimpleSwap] HYPE/ICP not supported via API, returning unavailable quote');
    return {
      provider: 'SimpleSwap',
      rate: 0,
      fee: 0.004,
      estimatedOutput: 0,
      available: false,
      error: 'HYPE and ICP swaps are not available via SimpleSwap API. Please use SimpleSwap website directly or try ChangeNOW.',
      providerFee: 0.004,
      kawSwapFee: 0,
      kycRisk: 'low',
      kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
    };
  }


  if (!API_KEYS.simpleswap) {
    return {
      provider: 'SimpleSwap',
      rate: 0,
      fee: 0.004,
      estimatedOutput: 0,
      available: false,
      error: 'SimpleSwap API key not configured',
      providerFee: 0.004,
      kawSwapFee: 0,
      kycRisk: 'low',
      kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
    };
  }

  try {
    // SimpleSwap uses specific ticker formats for multi-chain tokens
    // USDT on Ethereum: "usdterc20" (NOT "usdt" or "usdt-eth")
    // USDC on Ethereum: "usdcerc20"
    // USDT on BSC: "usdtbep20"
    // USDT on Tron: "usdttrc20"
    // Native tokens: Use lowercase symbol (MATIC -> "matic", AVAX -> "avax")
    // NOTE: SimpleSwap's old API doesn't support network suffixes for native tokens
    // It will automatically append network names, causing errors like "matic_matic"
    // For native tokens, we must use just the ticker symbol
    let fromTicker = fromToken.toLowerCase();
    let toTicker = toToken.toLowerCase();
    
    // Map USDT/USDC to SimpleSwap's ticker format based on chain
    if (toToken.toUpperCase() === 'USDT') {
      if (toChainId === 1 || toChainId === undefined) {
        toTicker = 'usdterc20'; // Ethereum USDT
      } else if (toChainId === 56) {
        toTicker = 'usdtbep20'; // BSC USDT
      } else if (toChainId === 728126428) {
        toTicker = 'usdttrc20'; // Tron USDT
      }
    } else if (toToken.toUpperCase() === 'USDC') {
      // SimpleSwap uses "usdc" for Ethereum USDC (not "usdcerc20")
      // Only BSC uses "usdcbep20"
      if (toChainId === 56) {
        toTicker = 'usdcbep20'; // BSC USDC
      }
      // For Ethereum (chainId 1 or undefined), keep "usdc" as-is
    }
    // For native tokens (MATIC, AVAX, ARB, OP), use lowercase symbol
    // SimpleSwap's old API doesn't support these with network suffixes
    // They may not be supported for all pairs (e.g., BTC -> MATIC may not work)
    
    // Same for fromToken if needed
    if (fromToken.toUpperCase() === 'USDT') {
      if (fromChainId === 1 || fromChainId === undefined) {
        fromTicker = 'usdterc20';
      } else if (fromChainId === 56) {
        fromTicker = 'usdtbep20';
      } else if (fromChainId === 728126428) {
        fromTicker = 'usdttrc20';
      }
    } else if (fromToken.toUpperCase() === 'USDC') {
      // SimpleSwap uses "usdc" for Ethereum USDC (not "usdcerc20")
      // Only BSC uses "usdcbep20"
      if (fromChainId === 56) {
        fromTicker = 'usdcbep20';
      }
      // For Ethereum (chainId 1 or undefined), keep "usdc" as-is
    } else if (fromToken.toUpperCase() === 'HYPE') {
      // SimpleSwap v4 API uses "hype" ticker with "hype" network (not "hype-hype")
      // The website URL shows "hype-hype" but the API uses separate ticker and network params
      fromTicker = 'hype'; // Always use "hype" ticker
    }
    
    // Handle HYPE for toToken
    if (toToken.toUpperCase() === 'HYPE') {
      // SimpleSwap v4 API uses "hype" ticker with "hype" network (not "hype-hype")
      toTicker = 'hype'; // Always use "hype" ticker
    }
    
    console.log('[SimpleSwap] Ticker mapping:', { 
      fromToken, 
      fromTicker, 
      fromChainId, 
      toToken, 
      toTicker, 
      toChainId 
    });

    // Get network suffixes for logging (not used in SimpleSwap API, but useful for debugging)
    const fromNetwork = getSimpleSwapNetworkSuffix(fromChainId);
    const toNetwork = getSimpleSwapNetworkSuffix(toChainId);
    
    // Use VPS proxy for consistency and API key security (bypasses IC HTTP outcall issues)
    // Use request deduplication to prevent redundant calls
    const cacheKey = getRequestCacheKey('SimpleSwap', fromTicker, toTicker, amount, fromNetwork, toNetwork);
    
    const data = await fetchWithDedup(cacheKey, async () => {
      // Try VPS proxy first if configured
      const proxyUrl = buildProxyUrl('simpleswap/quote');
      if (proxyUrl) {
        try {
          const url = new URL(proxyUrl);
          url.searchParams.set('currency_from', fromTicker);
          url.searchParams.set('currency_to', toTicker);
          url.searchParams.set('amount', amount.toString());
          // Pass network info if available (for VPS proxy to handle)
          if (fromNetwork) {
            url.searchParams.set('from_network', fromNetwork);
          }
          if (toNetwork) {
            url.searchParams.set('to_network', toNetwork);
          }
          
          console.log('[SimpleSwap] Fetching quote via VPS proxy:', { fromTicker, toTicker, amount, fromNetwork, toNetwork });
          const resp = await Promise.race([
            fetch(url.toString(), { method: 'GET' }),
            createTimeout(API_TIMEOUT)
          ]);
          
          if (!resp.ok) {
            const errorText = await resp.text().catch(() => '');
            // Try to parse error for minimum amount info
            try {
              const errorData = JSON.parse(errorText);
              if (errorData.description?.includes('Min:')) {
                const minMatch = errorData.description.match(/Min:\s*([\d.]+)/i);
                if (minMatch) {
                  throw new Error(`MIN_AMOUNT:${minMatch[1]}`);
                }
              }
            } catch {}
            throw new Error(`VPS proxy returned status ${resp.status}: ${errorText.substring(0, 200)}`);
          }
          const text = await resp.text();
          console.log('[SimpleSwap] VPS proxy response:', text);
          // Handle case where SimpleSwap returns plain number string
          const parsed = text.trim();
          // If it's a plain number (not JSON object), wrap it
          if (/^\d+\.?\d*$/.test(parsed)) {
            return { amount_to: parseFloat(parsed), estimated_amount: parseFloat(parsed) };
          }
          // Otherwise parse as JSON
          try {
            return JSON.parse(parsed);
          } catch {
            // If parsing fails, try to extract number from string
            const numMatch = parsed.match(/(\d+\.?\d*)/);
            if (numMatch) {
              return { amount_to: parseFloat(numMatch[1]), estimated_amount: parseFloat(numMatch[1]) };
            }
            throw new Error(`Invalid response format: ${parsed.substring(0, 100)}`);
          }
        } catch (vpsError) {
          console.warn('[SimpleSwap] VPS proxy failed, falling back to direct API:', vpsError);
          // Fall through to direct API call
        }
      }
      
      // Fallback to direct API (if proxy not configured)
      // For HYPE token, use SimpleSwap v4 API which uses ticker + network params
      // For other tokens, use old API format
      let url: string;
      if (fromTicker === 'hype' || toTicker === 'hype') {
        // Use v4 API for HYPE: tickerFrom, tickerTo, networkFrom, networkTo
        const networkFromParam = fromNetwork || fromTicker; // Use network or fallback to ticker
        const networkToParam = toNetwork || toTicker;
        url = `https://simpleswap.io/api/v4/estimates?amount=${amount}&fixed=false&networkFrom=${networkFromParam}&networkTo=${networkToParam}&tickerFrom=${fromTicker}&tickerTo=${toTicker}&reverse=false`;
      } else {
        // Old API format for other tokens
        url = `https://api.simpleswap.io/get_estimated?api_key=${API_KEYS.simpleswap}&currency_from=${fromTicker}&currency_to=${toTicker}&amount=${amount}&fixed=false`;
      }
      
      console.log('[SimpleSwap] Fetching quote via direct API:', { fromTicker, toTicker, amount, fromNetwork, toNetwork, url: url.substring(0, 100) + '...' });
      const resp = await Promise.race([
        fetch(url, { method: 'GET' }),
        createTimeout(API_TIMEOUT)
      ]);
      
      if (!resp.ok) {
        const errorText = await resp.text().catch(() => '');
        // Try to parse error for minimum amount info
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.description?.includes('Min:')) {
            const minMatch = errorData.description.match(/Min:\s*([\d.]+)/i);
            if (minMatch) {
              throw new Error(`MIN_AMOUNT:${minMatch[1]}`);
            }
          }
        } catch {}
        throw new Error(`SimpleSwap API returned status ${resp.status}: ${errorText.substring(0, 200)}`);
      }
      
      const data = await resp.json();
      console.log('[SimpleSwap] API response:', data);
      
      // Handle v4 API response format (has result.estimate)
      if (data.result && typeof data.result.estimate !== 'undefined') {
        return {
          amount_to: parseFloat(data.result.estimate),
          estimated_amount: parseFloat(data.result.estimate),
        };
      }
      
      return data;
    }).catch((error: any) => {
      // Handle errors from backend proxy
      const errorText = String(error.message || error);
      
      // Check for minimum amount errors (including MIN_AMOUNT: prefix)
      if (errorText.includes('MIN_AMOUNT:')) {
        const minAmount = parseFloat(errorText.split('MIN_AMOUNT:')[1]?.split(/[\s\n]/)[0] || '0');
        if (minAmount > 0) {
          return {
            provider: 'SimpleSwap',
            rate: 0,
            fee: 0.004,
            estimatedOutput: 0,
            available: false,
            error: `Minimum swap amount: ${minAmount.toFixed(8)} ${fromToken}. Your amount: ${amount} ${fromToken}`,
            minAmount: minAmount,
            providerFee: 0.004,
            kawSwapFee: 0,
            kycRisk: 'low',
            kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
          };
        }
      }
      
      if (errorText.toLowerCase().includes('minimum') || 
          errorText.toLowerCase().includes('too small') ||
          errorText.toLowerCase().includes('min amount') ||
          errorText.toLowerCase().includes('does not fall within the range')) {
        const minAmountMatch = errorText.match(/min:\s*([\d.]+)/i) || 
                              errorText.match(/minimum.*?([\d.]+)/i) || 
                              errorText.match(/min.*?([\d.]+)/i) ||
                              errorText.match(/([\d.]+)/);
        const minAmount = minAmountMatch ? parseFloat(minAmountMatch[1]) : null;
        
        return {
          provider: 'SimpleSwap',
          rate: 0,
          fee: 0.004,
          estimatedOutput: 0,
          available: false,
          error: minAmount 
            ? `Minimum swap amount: ${minAmount.toFixed(8)} ${fromToken}. Your amount: ${amount} ${fromToken}`
            : `Swap amount too small for ${fromToken} → ${toToken}. Try increasing the amount.`,
          minAmount: minAmount || undefined,
          providerFee: 0.004,
          kawSwapFee: 0,
          kycRisk: 'low',
          kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
        };
      }
      
      // Other errors (invalid pair, etc.)
      throw error; // Re-throw to be caught by outer catch block
    });
    
    // Handle errors from backend proxy - check if data is an object first
    if (data && typeof data === 'object' && 'error' in data && !data.available) {
      return data as SwapQuote;
    }
    
    // Handle case where response is a plain number or has numeric fields
    let estimatedOutput: number;
    if (typeof data === 'number') {
      estimatedOutput = data;
    } else if (typeof data === 'object' && data !== null) {
      estimatedOutput = parseFloat(String(data.amount_to || data.estimated_amount || data.amount || '0'));
    } else if (typeof data === 'string') {
      const numMatch = data.match(/(\d+\.?\d*)/);
      estimatedOutput = numMatch ? parseFloat(numMatch[1]) : 0;
    } else {
      estimatedOutput = 0;
    }

    if (!estimatedOutput || estimatedOutput <= 0 || isNaN(estimatedOutput)) {
      return {
        provider: 'SimpleSwap',
        rate: 0,
        fee: 0.004,
        estimatedOutput: 0,
        available: false,
        error: 'Invalid quote from SimpleSwap',
        providerFee: 0.004,
        kawSwapFee: 0,
        kycRisk: 'low',
        kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
      };
    }

    // SimpleSwap fee is 0.4% (0.004) - affiliate commission
    const fee = 0.004;
    const feeInfo = applyKawSwapFee(fee, estimatedOutput, true); // true = affiliate provider
    const rate = estimatedOutput / amount;

    return {
      provider: 'SimpleSwap',
      available: true,
      rate,
      estimatedOutput: feeInfo.adjustedOutput,
      fee: feeInfo.totalFee,
      providerFee: feeInfo.providerFee,
      kawSwapFee: feeInfo.kawSwapFee,
      isCrossChain: fromToken.toUpperCase() !== toToken.toUpperCase(),
      kycRisk: 'low',
      kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
    };
  } catch (error: any) {
    console.error('SimpleSwap quote error:', error);
    
    const errorMessage = error.message || error.toString() || '';
    const errorText = errorMessage.toLowerCase();
    
    // Check for network/CORS errors
    if (errorText.includes('cors') || 
        errorText.includes('access-control') || 
        errorText.includes('blocked by cors') ||
        (error.name === 'TypeError' && errorText.includes('failed to fetch'))) {
      return {
        provider: 'SimpleSwap',
        rate: 0,
        fee: 0.004,
        estimatedOutput: 0,
        available: false,
        error: 'Network error - API request blocked',
        providerFee: 0.004,
        kawSwapFee: 0,
        kycRisk: 'low',
        kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
      };
    }
    
    // Check for minimum amount errors
    if (errorText.includes('minimum') || errorText.includes('too small') || 
        errorText.includes('min amount')) {
      const minAmountMatch = errorMessage.match(/minimum.*?([\d.]+)/i) || 
                            errorMessage.match(/min.*?([\d.]+)/i);
      const minAmount = minAmountMatch ? parseFloat(minAmountMatch[1]) : undefined;
      
      return {
        provider: 'SimpleSwap',
        rate: 0,
        fee: 0.004,
        estimatedOutput: 0,
        available: false,
        error: minAmount 
          ? `Minimum swap amount: ${minAmount.toFixed(8)} ${fromToken}. Your amount: ${amount} ${fromToken}`
          : 'Swap amount too small - increase amount and try again',
        minAmount: minAmount || undefined,
        providerFee: 0.004,
        kawSwapFee: 0,
        kycRisk: 'low',
        kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
      };
    }
    
    return {
      provider: 'SimpleSwap',
      rate: 0,
      fee: 0.004,
      estimatedOutput: 0,
      available: false,
      error: errorMessage || 'Failed to fetch SimpleSwap quote',
      providerFee: 0.004,
      kawSwapFee: 0,
      kycRisk: 'low',
      kycMessage: 'SimpleSwap typically requires no KYC for most swaps',
    };
  }
}

// Fetch quote from ChangeNOW
/**
 * Map chain ID to ChangeNOW network name
 * ChangeNOW requires network parameter for multi-chain tokens (e.g., USDT, USDC)
 */
function getChangeNOWNetworkName(chainId?: number): string | undefined {
  if (!chainId) return undefined;
  
  // Import CHAIN_IDS dynamically to avoid circular dependencies
  const CHAIN_IDS = {
    ETHEREUM: 1,
    BSC: 56,
    BASE: 8453,
    POLYGON: 137,
    ARBITRUM: 42161,
    OPTIMISM: 10,
    AVALANCHE: 43114,
    TRON: 728126428,
    HYPERLIQUID: 998,
  };
  
  // ChangeNOW uses specific network names (not always the chain name)
  // 'matic' for Polygon, 'op' for Optimism, 'avax' for Avalanche
  const networkMap: Record<number, string> = {
    [CHAIN_IDS.ETHEREUM]: 'eth',
    [CHAIN_IDS.BSC]: 'bsc',
    [CHAIN_IDS.BASE]: 'base',
    [CHAIN_IDS.POLYGON]: 'matic', // ChangeNOW uses 'matic' not 'polygon'
    [CHAIN_IDS.ARBITRUM]: 'arbitrum',
    [CHAIN_IDS.OPTIMISM]: 'op', // ChangeNOW uses 'op' not 'optimism'
    [CHAIN_IDS.AVALANCHE]: 'cchain', // ChangeNOW uses 'cchain' for Avalanche C-Chain (NOT 'avax' or 'avalanche')
    [CHAIN_IDS.TRON]: 'tron',
    [CHAIN_IDS.HYPERLIQUID]: 'hyperliquid',
    [CHAIN_IDS.BSC]: 'bsc', // BNB network on ChangeNOW
    9995: 'icp', // Internet Computer (ICP chain ID from tokenRegistry)
    9994: 'ton', // TON (The Open Network)
    9993: 'sui', // SUI
  };
  
  // Special handling for native tokens that use their own network name
  // ADA uses 'ada', ZEC uses 'zec', DASH uses 'dash', XMR uses 'xmr'
  // These don't have chain IDs, so check by token symbol (handled in executeChangeNOWSwap)
  
  return networkMap[chainId];
}

async function fetchChangeNOWQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  destinationAddress?: string,
  fromChainId?: number,
  toChainId?: number
): Promise<SwapQuote> {
  const provider = getProviderByName('ChangeNOW');
  if (!provider) {
    throw new Error('ChangeNOW provider not found');
  }

  // Check if API key is available
  if (!API_KEYS.changenow) {
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee + KAWSWAP_DEX_FEE,
      estimatedOutput: 0,
      available: false,
      error: 'ChangeNOW API key not configured',
      providerFee: provider.fee,
      kawSwapFee: KAWSWAP_DEX_FEE,
      kycRisk: 'medium',
      kycMessage: 'May require KYC verification - you can cancel if required',
    };
  }

  try {
    // Map token symbols to ChangeNOW format
    // ChangeNOW uses ticker symbols (RVN, ETH, BTC, etc.)
    let fromTicker = fromToken.toLowerCase();
    let toTicker = toToken.toLowerCase();
    
    // HYPE token handling - ChangeNOW uses "hype" ticker with "hype" network
    if (fromToken.toUpperCase() === 'HYPE') {
      fromTicker = 'hype';
    }
    if (toToken.toUpperCase() === 'HYPE') {
      toTicker = 'hype';
    }
    
    // Get network names for multi-chain tokens
    let fromNetwork = getChangeNOWNetworkName(fromChainId);
    let toNetwork = getChangeNOWNetworkName(toChainId);
    
    // For HYPE token, always use "hype" as network
    if (fromToken.toUpperCase() === 'HYPE') {
      fromNetwork = 'hype';
    }
    if (toToken.toUpperCase() === 'HYPE') {
      toNetwork = 'hype';
    }
    
    // For BNB token, always use "bsc" as network
    if (fromToken.toUpperCase() === 'BNB') {
      fromNetwork = 'bsc';
    }
    if (toToken.toUpperCase() === 'BNB') {
      toNetwork = 'bsc';
    }
    
    // For AVAX token, always use "cchain" as network (Avalanche C-Chain)
    if (fromToken.toUpperCase() === 'AVAX') {
      fromNetwork = 'cchain';
    }
    if (toToken.toUpperCase() === 'AVAX') {
      toNetwork = 'cchain';
    }
    
    // For TRX token, ChangeNOW auto-uses "trx" network - don't override
    // ChangeNOW automatically detects TRX network from currency ticker
    // Only set if not already set by getChangeNOWNetworkName
    if ((fromToken.toUpperCase() === 'TRX' || fromToken.toUpperCase() === 'TRON') && !fromNetwork) {
      fromNetwork = 'trx';
    }
    if ((toToken.toUpperCase() === 'TRX' || toToken.toUpperCase() === 'TRON') && !toNetwork) {
      toNetwork = 'trx';
    }
    
    // For native tokens that use their own network names on ChangeNOW
    if (fromToken.toUpperCase() === 'ADA') {
      fromNetwork = 'ada';
    }
    if (toToken.toUpperCase() === 'ADA') {
      toNetwork = 'ada';
    }
    if (fromToken.toUpperCase() === 'ZEC') {
      fromNetwork = 'zec';
    }
    if (toToken.toUpperCase() === 'ZEC') {
      toNetwork = 'zec';
    }
    if (fromToken.toUpperCase() === 'DASH') {
      fromNetwork = 'dash';
    }
    if (toToken.toUpperCase() === 'DASH') {
      toNetwork = 'dash';
    }
    if (fromToken.toUpperCase() === 'XMR') {
      fromNetwork = 'xmr';
    }
    if (toToken.toUpperCase() === 'XMR') {
      toNetwork = 'xmr';
    }

    // ChangeNOW v2 getExchangeAmount - prefer VPS proxy if configured, fallback to IC backend
    // Format: JSON-RPC 2.0 POST to /v2/exchange with getExchangeAmount method
    // Use request deduplication to prevent redundant calls
    const cacheKey = getRequestCacheKey('ChangeNOW', fromTicker, toTicker, amount);
    
    const data = await fetchWithDedup(cacheKey, async () => {
      // Try VPS proxy first if configured (more reliable than IC HTTP outcalls)
      const proxyUrl = buildProxyUrl('changenow/quote');
      if (proxyUrl) {
        try {
          // Build params object with network parameters if available
          const params: any = {
            fromCurrency: fromTicker,
            toCurrency: toTicker,
            amountFrom: amount.toString()
          };
          
          // Add network parameters for multi-chain tokens (required for HYPE)
          if (fromNetwork) {
            params.fromNetwork = fromNetwork;
          }
          if (toNetwork) {
            params.toNetwork = toNetwork;
          }
          
          // For HYPE, ensure network is set to "hype"
          if (fromToken.toUpperCase() === 'HYPE' && !params.fromNetwork) {
            params.fromNetwork = 'hype';
          }
          if (toToken.toUpperCase() === 'HYPE' && !params.toNetwork) {
            params.toNetwork = 'hype';
          }
          
          const jsonRpcBody = JSON.stringify({
            jsonrpc: '2.0',
            id: Date.now().toString(),
            method: 'getExchangeAmount',
            params
          });
          
          console.log('[ChangeNOW] Fetching quote via VPS proxy:', { fromTicker, toTicker, amount, fromNetwork, toNetwork });
          // Increase timeout for ChangeNOW (can be slow for multi-chain tokens)
          const CHANGENOW_TIMEOUT = 25000; // 25 seconds for multi-chain token queries
          const resp = await Promise.race([
            fetch(proxyUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: jsonRpcBody
            }),
            createTimeout(CHANGENOW_TIMEOUT)
          ]);
          if (!resp.ok) {
            const errorText = await resp.text().catch(() => '');
            throw new Error(`VPS proxy returned status ${resp.status}: ${errorText.substring(0, 200)}`);
          }
          const data = await resp.json();
          console.log('[ChangeNOW] VPS proxy response:', data);
          // ChangeNOW v2 REST API returns direct object, not JSON-RPC
          // Format: { fromAmount, toAmount, fromCurrency, toCurrency, ... }
          // OR error: { error: "...", message: "..." }
          if (data.error) {
            throw new Error(data.message || data.error || 'ChangeNOW API error');
          }
          return data;
        } catch (vpsError) {
          console.warn('[ChangeNOW] VPS proxy failed:', vpsError);
          // If network parameters are required, don't fall back to backend proxy (it doesn't support them)
          if (fromNetwork || toNetwork) {
            throw new Error(`ChangeNOW VPS proxy failed and backend proxy doesn't support network parameters: ${vpsError.message}`);
          }
          // Only fall back to backend proxy if no network parameters are needed
        }
      }
      
      // Fallback to IC backend proxy (only if no network parameters required)
      if (fromNetwork || toNetwork) {
        throw new Error('ChangeNOW requires network parameters but VPS proxy is not configured. Please configure VPS proxy.');
      }
      
      const { backend } = await import('../declarations/backend');
      if (!backend) {
        console.error('[ChangeNOW] Backend canister not available');
        throw new Error('Backend canister not available');
      }
      
      console.log('[ChangeNOW] Fetching quote via IC backend proxy:', { fromTicker, toTicker, amount });
      const result = await backend.proxyChangeNOWQuote(
        fromTicker,
        toTicker,
        amount.toString(),
        API_KEYS.changenow
      );
      
      console.log('[ChangeNOW] Backend proxy response:', result);
      
      if ('err' in result) {
        console.error('[ChangeNOW] Backend proxy error:', result.err);
        throw new Error(result.err);
      }
      
      const responseData = JSON.parse(result.ok);
      console.log('[ChangeNOW] Parsed response:', responseData);
      
      // Parse ChangeNOW v2 JSON-RPC response
      // Format: { jsonrpc: "2.0", id: "...", result: { estimatedAmount: "...", ... } }
      // Or error: { jsonrpc: "2.0", id: "...", error: { code: ..., message: ... } }
      if (responseData.error) {
        const errorMsg = responseData.error.message || `ChangeNOW API error: ${JSON.stringify(responseData.error)}`;
        // Check for minimum amount errors
        if (responseData.error.message?.includes('deposit_too_small') || responseData.error.message?.includes('min amount')) {
          const minAmountMatch = responseData.error.message?.match(/min amount ([\d.]+)/i) || 
                                responseData.error.message?.match(/([\d.]+)/);
          const minAmount = minAmountMatch ? parseFloat(minAmountMatch[1]) : null;
          
          if (minAmount) {
            throw new Error(`MIN_AMOUNT:${minAmount}`);
          }
        }
        throw new Error(errorMsg);
      }
      
      return responseData.result || responseData;
    });

    // ChangeNOW v2 REST API returns direct object, not JSON-RPC
    // Format: { fromAmount, toAmount, fromCurrency, toCurrency, ... }
    const result = data;
    const outputAmount = parseFloat(result.toAmount || result.estimatedAmount || result.amount || '0');
    const exchangeRate = outputAmount > 0 && amount > 0 ? outputAmount / amount : 0;
    
    // Check for KYC requirement in response
    const requiresKYC = result.kycRequired === true || result.requiresKYC === true;

    // Validate response
    if (outputAmount <= 0 || exchangeRate <= 0) {
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee + KAWSWAP_DEX_FEE,
        estimatedOutput: 0,
        available: false,
        error: 'No liquidity available for this pair',
        providerFee: provider.fee,
        kawSwapFee: KAWSWAP_DEX_FEE,
        kycRisk: 'medium',
        kycMessage: requiresKYC ? 'KYC verification required for this transaction' : 'May require KYC verification',
      };
    }

    // Calculate rate and apply fees
    // ChangeNOW is an AFFILIATE provider - we earn 0.4% commission, no markup added
    const rate = exchangeRate || (outputAmount / amount);
    const feeBreakdown = applyKawSwapFee(provider.fee, outputAmount, true); // true = affiliate, no markup

    return {
      provider: provider.name,
      rate,
      fee: feeBreakdown.totalFee,
      estimatedOutput: feeBreakdown.adjustedOutput, // No markup applied, users get full output
      available: true,
      providerFee: feeBreakdown.providerFee,
      kawSwapFee: feeBreakdown.kawSwapFee, // Shows 0.4% commission (for transparency)
      isCrossChain: true, // ChangeNOW handles cross-chain swaps
      kycRisk: requiresKYC ? 'high' : 'medium',
      kycMessage: requiresKYC 
        ? 'KYC verification required for this transaction - you can cancel if needed'
        : 'May require KYC verification - you can cancel if required',
    };
  } catch (error: any) {
    console.error('ChangeNOW quote error:', error);
    
    let errorMessage = 'Failed to fetch quote';
    let minAmount: number | undefined;
    
    // Check for minimum amount errors in the error message
    if (error.message.includes('deposit_too_small') || error.message.includes('min amount')) {
      // Extract minimum amount from error message
      const minAmountMatch = error.message.match(/min amount ([\d.]+)/i) || 
                            error.message.match(/Out of min amount ([\d.]+)/i) ||
                            error.message.match(/([\d.]+)/);
      if (minAmountMatch) {
        minAmount = parseFloat(minAmountMatch[1]);
        errorMessage = `Minimum swap amount: ${minAmount.toFixed(8)} ${fromToken}. Your amount: ${amount} ${fromToken}`;
      } else {
        errorMessage = 'Swap amount below minimum - increase amount and try again';
      }
    } else if (error.message.includes('timeout')) {
      errorMessage = 'API request timed out';
    } else if (error.message.includes('status')) {
      // Try to parse status errors for minimum amount info
      try {
        const errorMatch = error.message.match(/\{.*"error":"deposit_too_small".*"message":"[^"]*min amount ([\d.]+)[^"]*"/i);
        if (errorMatch) {
          minAmount = parseFloat(errorMatch[1]);
          errorMessage = `Minimum swap amount: ${minAmount.toFixed(8)} ${fromToken}. Your amount: ${amount} ${fromToken}`;
        } else {
          errorMessage = 'API temporarily unavailable';
        }
      } catch {
        errorMessage = 'API temporarily unavailable';
      }
    } else if (error.message.includes('key')) {
      errorMessage = 'API key invalid or missing';
    }
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee + KAWSWAP_DEX_FEE,
      estimatedOutput: 0,
      available: false,
      error: errorMessage,
      minAmount: minAmount,
      providerFee: provider.fee,
      kawSwapFee: KAWSWAP_DEX_FEE,
      kycRisk: 'medium',
      kycMessage: 'May require KYC verification',
    };
  }
}

// Fetch quotes from all providers and select optimal route with enhanced error handling
export async function fetchOptimalRoute(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number = 1
): Promise<SwapQuote | null> {
  try {
    // Fetch quotes from all providers in parallel with individual error handling
    const quotes = await Promise.all([
      fetch0xQuote(fromToken, toToken, amount, chainId),
      fetchParaswapQuote(fromToken, toToken, amount, chainId),
      fetchKyberSwapQuote(fromToken, toToken, amount, chainId),
      fetchOpenOceanQuote(fromToken, toToken, amount, chainId),
      fetch1inchQuote(fromToken, toToken, amount, chainId),
      // fetchRavencoinBridgeQuote(fromToken, toToken, amount), // REMOVED - Not usable
    ]);

    // Filter available quotes
    const availableQuotes = quotes.filter((q) => q.available);

    if (availableQuotes.length === 0) {
      // Show specific errors from providers
      const errors = quotes.filter(q => q.error).map(q => `${q.provider}: ${q.error}`);
      
      toast.error('No Liquidity Available', {
        description: errors.length > 0 
          ? `All providers unavailable. ${errors[0]}`
          : 'All liquidity providers are currently unavailable. Please try again later.',
        duration: 6000,
      });
      return null;
    }

    // Select optimal quote (highest estimated output)
    const optimalQuote = availableQuotes.reduce((best, current) =>
      current.estimatedOutput > best.estimatedOutput ? current : best
    );

    // Show info about routing with provider details
    toast.info('Optimal Route Found', {
      description: `Using ${optimalQuote.provider} for best pricing (${(optimalQuote.fee * 100).toFixed(2)}% fee)`,
      duration: 4000,
    });

    return optimalQuote;
  } catch (error: any) {
    console.error('Failed to fetch optimal route:', error);
    toast.error('Routing Failed', {
      description: 'Failed to fetch liquidity quotes. Please check your connection and try again.',
      duration: 5000,
    });
    return null;
  }
}

// Execute swap via 0x Protocol
async function execute0xSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  chainId: number = 1
): Promise<{ txHash: string; success: boolean }> {
  const provider = LIQUIDITY_PROVIDERS[0];
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  const sellAmount = Math.floor(amount * 1e18).toString();

  try {
    // Get swap transaction data from 0x
    const quoteResponse = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/swap/v1/quote?buyToken=${toAddress}&sellToken=${fromAddress}&sellAmount=${sellAmount}&chainId=${chainId}&takerAddress=${walletAddress}`,
        { 
          method: 'GET', 
          headers: { 
            'Content-Type': 'application/json',
            '0x-api-key': API_KEYS['0x'],
          } 
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!quoteResponse.ok) {
      throw new Error(`0x API returned status ${quoteResponse.status}`);
    }

    const quoteData = await quoteResponse.json();

    // Get swap transaction
    const swapResponse = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/swap/v1/swap`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            '0x-api-key': API_KEYS['0x'],
          },
          body: JSON.stringify({
            ...quoteData,
            takerAddress: walletAddress,
          }),
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!swapResponse.ok) {
      throw new Error(`0x swap API returned status ${swapResponse.status}`);
    }

    const swapData = await swapResponse.json();

    // Sign and submit transaction with full transaction parameters
    const txHash = await signWeb3Transaction({
      from: walletAddress,
      to: swapData.to,
      value: swapData.value || '0',
      data: swapData.data,
      gas: swapData.gas,
      gasPrice: swapData.gasPrice,
      maxFeePerGas: swapData.maxFeePerGas,
      maxPriorityFeePerGas: swapData.maxPriorityFeePerGas,
    });

    return { txHash, success: true };
  } catch (error: any) {
    console.error('0x swap execution error:', error);
    throw new Error(error.message || 'Failed to execute 0x swap');
  }
}

// Execute swap via Velora (ParaSwap) v6.2
async function executeParaswapSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  chainId: number = 1
): Promise<{ txHash: string; success: boolean }> {
  const provider = LIQUIDITY_PROVIDERS[1];
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  const amountWei = Math.floor(amount * 1e18).toString();

  try {
    // Get price route (v6.2)
    const priceResponse = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/prices?srcToken=${fromAddress}&destToken=${toAddress}&amount=${amountWei}&srcDecimals=18&destDecimals=18&side=SELL&network=${chainId}`,
        { 
          method: 'GET', 
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-Id': 'KawSwap',
          } 
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!priceResponse.ok) {
      throw new Error(`Velora API returned status ${priceResponse.status}`);
    }

    const priceData = await priceResponse.json();

    // Get swap transaction (v6.2 uses /swap endpoint)
    const txResponse = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/swap`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-Id': 'KawSwap',
          },
          body: JSON.stringify({
            srcToken: fromAddress,
            destToken: toAddress,
            srcAmount: amountWei,
            destAmount: priceData.priceRoute?.destAmount || priceData.destAmount,
            priceRoute: priceData.priceRoute || priceData,
            userAddress: walletAddress,
            slippage: 1, // 1% slippage
          }),
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!txResponse.ok) {
      throw new Error(`Velora swap API returned status ${txResponse.status}`);
    }

    const txData = await txResponse.json();

    // Sign and submit transaction with full transaction parameters
    const txHash = await signWeb3Transaction({
      from: walletAddress,
      to: txData.to,
      value: txData.value || '0',
      data: txData.data,
      gas: txData.gas,
      gasPrice: txData.gasPrice,
      maxFeePerGas: txData.maxFeePerGas,
      maxPriorityFeePerGas: txData.maxPriorityFeePerGas,
    });

    return { txHash, success: true };
  } catch (error: any) {
    console.error('Velora swap execution error:', error);
    throw new Error(error.message || 'Failed to execute Velora swap');
  }
}

// Execute swap via 1inch
async function execute1inchSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  chainId: number = 1
): Promise<{ txHash: string; success: boolean }> {
  const provider = getProviderByName('1inch Aggregator');
  if (!provider) {
    throw new Error('1inch provider not found');
  }
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  const fromDecimals = getTokenDecimals(fromToken);
  const amountBase = toBaseUnits(amount, fromDecimals).toString();

  try {
    // 1inch swap via VPS proxy (bypasses IC HTTP outcall issues and CORS)
    const proxyUrl = buildProxyUrl('1inch/swap');
    let swapData: any;
    
    if (proxyUrl) {
      try {
        const url = new URL(proxyUrl);
        url.searchParams.set('chainId', String(chainId));
        url.searchParams.set('src', fromAddress);
        url.searchParams.set('dst', toAddress);
        url.searchParams.set('amount', amountBase);
        url.searchParams.set('from', walletAddress);
        url.searchParams.set('slippage', '1');
        url.searchParams.set('fee', '0.4');
        url.searchParams.set('referrer', KAWSWAP_FEE_WALLET_ETH);
        
        console.log('[1inch] Getting swap transaction via VPS proxy:', { chainId, fromAddress, toAddress, amountBase, walletAddress });
        const resp = await Promise.race([
          fetch(url.toString(), { method: 'GET' }),
          createTimeout(API_TIMEOUT)
        ]);
        
        if (!resp.ok) throw new Error(`VPS proxy returned status ${resp.status}`);
        swapData = await resp.json();
        console.log('[1inch] VPS proxy response:', swapData);
        
        // Check for errors
        if (swapData.error) {
          throw new Error(swapData.error);
        }
      } catch (vpsError) {
        console.warn('[1inch] VPS proxy failed, falling back to backend:', vpsError);
        // Fall through to backend fallback
      }
    }
    
    // Fallback to backend (if proxy not configured or failed)
    if (!swapData) {
      const { backend } = await import('../declarations/backend');
      if (!backend) throw new Error('Backend canister not available');
      const result = await backend.proxy1inchSwap(
        BigInt(chainId),
        fromAddress,
        toAddress,
        amountBase,
        walletAddress,
        '1', // slippage
        '0.4', // fee
        KAWSWAP_FEE_WALLET_ETH // referrer
      );
      
      if ('err' in result) {
        throw new Error(result.err);
      }
      
      swapData = JSON.parse(result.ok);
    }

    // Sign and submit transaction with full transaction parameters
    const txHash = await signWeb3Transaction({
      from: walletAddress,
      to: swapData.tx.to,
      value: swapData.tx.value || '0',
      data: swapData.tx.data,
      gas: swapData.tx.gas,
      gasPrice: swapData.tx.gasPrice,
      maxFeePerGas: swapData.tx.maxFeePerGas,
      maxPriorityFeePerGas: swapData.tx.maxPriorityFeePerGas,
    });

    return { txHash, success: true };
  } catch (error: any) {
    console.error('1inch swap execution error:', error);
    throw new Error(error.message || 'Failed to execute 1inch swap');
  }
}

// Execute swap via KyberSwap
async function executeKyberSwapSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  chainId: number = 1
): Promise<{ txHash: string; success: boolean }> {
  const provider = LIQUIDITY_PROVIDERS[2];
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  const amountWei = Math.floor(amount * 1e18).toString();

  try {
    // Get route
    const routeResponse = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/v1/route?tokenIn=${fromAddress}&tokenOut=${toAddress}&amountIn=${amountWei}&saveGas=0&gasInclude=1&to=${chainId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!routeResponse.ok) {
      throw new Error(`KyberSwap API returned status ${routeResponse.status}`);
    }

    const routeData = await routeResponse.json();

    // Build transaction (KyberSwap requires building the transaction from route)
    // For now, we'll use a simplified approach - in production, use KyberSwap's transaction builder
    const txHash = await signWeb3Transaction({
      from: walletAddress,
      to: routeData.routerAddress || routeData.to || '0x0000000000000000000000000000000000000000',
      value: routeData.value || '0',
      data: routeData.encodedSwapData || routeData.data || '0x',
      gas: routeData.gas,
      gasPrice: routeData.gasPrice,
      maxFeePerGas: routeData.maxFeePerGas,
      maxPriorityFeePerGas: routeData.maxPriorityFeePerGas,
    });

    return { txHash, success: true };
  } catch (error: any) {
    console.error('KyberSwap swap execution error:', error);
    throw new Error(error.message || 'Failed to execute KyberSwap swap');
  }
}

// Execute swap via OpenOcean
async function executeOpenOceanSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  chainId: number = 1
): Promise<{ txHash: string; success: boolean }> {
  const provider = LIQUIDITY_PROVIDERS[3];
  const fromAddress = getTokenAddress(fromToken, chainId);
  const toAddress = getTokenAddress(toToken, chainId);
  const amountWei = Math.floor(amount * 1e18).toString();

  try {
    // Get swap data (v4 endpoint - no /v3/ in path)
    const swapResponse = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/${chainId}/swap?inTokenAddress=${fromAddress}&outTokenAddress=${toAddress}&amount=${amountWei}&gasPrice=5&slippage=1&account=${walletAddress}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!swapResponse.ok) {
      throw new Error(`OpenOcean API returned status ${swapResponse.status}`);
    }

    const swapData = await swapResponse.json();

    // Sign and submit transaction with full transaction parameters
    const txHash = await signWeb3Transaction({
      from: walletAddress,
      to: swapData.to,
      value: swapData.value || '0',
      data: swapData.data,
      gas: swapData.gas,
      gasPrice: swapData.gasPrice,
      maxFeePerGas: swapData.maxFeePerGas,
      maxPriorityFeePerGas: swapData.maxPriorityFeePerGas,
    });

    return { txHash, success: true };
  } catch (error: any) {
    console.error('OpenOcean swap execution error:', error);
    throw new Error(error.message || 'Failed to execute OpenOcean swap');
  }
}

// Ravencoin Bridge - REMOVED - Not usable
// Function removed as Ravencoin Bridge is not operational and not usable

// Execute swap via Wormhole (cross-chain bridge)
async function executeWormholeSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  fromChainId: number = 1,
  toChainId: number = 1,
  destinationAddress?: string
): Promise<{ txHash: string; success: boolean }> {
  const provider = getProviderByName('Wormhole');
  if (!provider) {
    throw new Error('Wormhole provider not found');
  }
  const fromAddress = getTokenAddress(fromToken, fromChainId);
  const toAddress = getTokenAddress(toToken, toChainId);
  const amountWei = Math.floor(amount * 1e18).toString();

  try {
    // Use Wormhole SDK for execution
    const wh = await getWormholeSDK();
    if (!wh) {
      throw new Error('Wormhole SDK not available');
    }

    // Map chain IDs to Wormhole chain names
    const chainNameMap: Record<number, string> = {
      1: 'Ethereum',
      8453: 'Base',
      56: 'Bsc',
    };

    const fromChainName = chainNameMap[fromChainId];
    const toChainName = chainNameMap[toChainId];

    if (!fromChainName || !toChainName) {
      throw new Error(`Unsupported chain: ${fromChainId} or ${toChainId}`);
    }

    // Get chain contexts
    const fromChain = wh.getChain(fromChainName);
    const toChain = wh.getChain(toChainName);

    if (!fromChain || !toChain) {
      throw new Error(`Chain not available: ${fromChainName} or ${toChainName}`);
    }

    // For now, use placeholder API call until SDK execution is fully implemented
    // TODO: Implement full SDK-based execution
    const swapResponse = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/v1/swap`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fromToken: fromAddress,
            toToken: toAddress,
            amount: amountWei,
            fromChain: fromChainId,
            toChain: toChainId,
            recipient: destinationAddress || walletAddress,
          }),
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!swapResponse.ok) {
      throw new Error(`Wormhole API returned status ${swapResponse.status}`);
    }

    const swapData = await swapResponse.json();

    // Sign and submit transaction
    const txHash = await signWeb3Transaction({
      from: walletAddress,
      to: swapData.to,
      value: swapData.value || '0',
      data: swapData.data,
      gas: swapData.gas,
      gasPrice: swapData.gasPrice,
      maxFeePerGas: swapData.maxFeePerGas,
      maxPriorityFeePerGas: swapData.maxPriorityFeePerGas,
    });

    return { txHash, success: true };
  } catch (error: any) {
    console.error('Wormhole swap execution error:', error);
    throw new Error(error.message || 'Failed to execute Wormhole swap');
  }
}

// Execute swap via Across Protocol (cross-chain bridge)
async function executeAcrossSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  fromChainId: number = 1,
  toChainId: number = 1,
  destinationAddress?: string
): Promise<{ txHash: string; success: boolean }> {
  const provider = getProviderByName('Across Protocol');
  if (!provider) {
    throw new Error('Across Protocol not available. Set VITE_ACROSS_INTEGRATOR_ID in .env');
  }
  const fromAddress = getTokenAddress(fromToken, fromChainId);
  const toAddress = getTokenAddress(toToken, toChainId);
  const amountWei = Math.floor(amount * 1e18).toString();

  try {
    // Use Across SDK for execution
    const acrossClient = await getAcrossSDK();
    if (!acrossClient) {
      throw new Error('Across SDK not available. Set VITE_ACROSS_INTEGRATOR_ID in .env');
    }

    // Map chain IDs to viem chains
    const chainMap: Record<number, any> = {
      1: mainnet,
      8453: base,
      42161: arbitrum,
    };

    const originChain = chainMap[fromChainId];
    const destinationChain = chainMap[toChainId];

    if (!originChain || !destinationChain) {
      throw new Error(`Unsupported chain: ${fromChainId} or ${toChainId}`);
    }

    // Use Across SDK to execute swap
    // Note: SDK execution methods may vary - adjust based on actual SDK API
    // For now, use placeholder until SDK execution is fully implemented
    // TODO: Implement full SDK-based execution using acrossClient.executeQuote()
    
    const swapResponse = await Promise.race([
      fetch(
        `${provider.apiEndpoint}/v1/swap`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            originToken: fromAddress,
            destinationToken: toAddress,
            amount: amountWei,
            originChainId: fromChainId,
            destinationChainId: toChainId,
            recipient: destinationAddress || walletAddress,
          }),
        }
      ),
      createTimeout(API_TIMEOUT),
    ]);

    if (!swapResponse.ok) {
      throw new Error(`Across API returned status ${swapResponse.status}`);
    }

    const swapData = await swapResponse.json();

    // Sign and submit transaction
    const txHash = await signWeb3Transaction({
      from: walletAddress,
      to: swapData.to,
      value: swapData.value || '0',
      data: swapData.data,
      gas: swapData.gas,
      gasPrice: swapData.gasPrice,
      maxFeePerGas: swapData.maxFeePerGas,
      maxPriorityFeePerGas: swapData.maxPriorityFeePerGas,
    });

    return { txHash, success: true };
  } catch (error: any) {
    console.error('Across swap execution error:', error);
    throw new Error(error.message || 'Failed to execute Across swap');
  }
}

// Execute swap via SimpleSwap (AFFILIATE - Earns 0.4% commission automatically!)
async function executeSimpleSwapSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  destinationAddress: string,
  fromChainId: number = 1,
  toChainId: number = 1
): Promise<{ txHash: string; success: boolean; depositAddress?: string; exchangeId?: string }> {
  const provider = getProviderByName('SimpleSwap');
  if (!provider) {
    throw new Error('SimpleSwap provider not found');
  }

  if (!API_KEYS.simpleswap) {
    throw new Error('SimpleSwap API key not configured');
  }

  // Validate destination address
  if (!destinationAddress) {
    throw new Error('Destination address is required for SimpleSwap swaps');
  }

  if (isPlaceholderAddress(destinationAddress, toToken)) {
    throw new Error('Please enter a real destination address. Placeholder addresses cannot be used for actual swaps.');
  }

  try {
    // Use same ticker mapping as quote function
    let fromTicker = fromToken.toLowerCase();
    let toTicker = toToken.toLowerCase();
    
    // Map USDT/USDC to SimpleSwap's ticker format based on chain
    if (toToken.toUpperCase() === 'USDT') {
      if (toChainId === 1 || toChainId === undefined) {
        toTicker = 'usdterc20'; // Ethereum USDT
      } else if (toChainId === 56) {
        toTicker = 'usdtbep20'; // BSC USDT
      } else if (toChainId === 728126428) {
        toTicker = 'usdttrc20'; // Tron USDT
      }
    } else if (toToken.toUpperCase() === 'USDC') {
      // SimpleSwap uses "usdc" for Ethereum USDC (not "usdcerc20")
      // Only BSC uses "usdcbep20"
      if (toChainId === 56) {
        toTicker = 'usdcbep20'; // BSC USDC
      }
      // For Ethereum (chainId 1 or undefined), keep "usdc" as-is
    }
    
    if (fromToken.toUpperCase() === 'USDT') {
      if (fromChainId === 1 || fromChainId === undefined) {
        fromTicker = 'usdterc20';
      } else if (fromChainId === 56) {
        fromTicker = 'usdtbep20';
      } else if (fromChainId === 728126428) {
        fromTicker = 'usdttrc20';
      }
    } else if (fromToken.toUpperCase() === 'USDC') {
      // SimpleSwap uses "usdc" for Ethereum USDC (not "usdcerc20")
      // Only BSC uses "usdcbep20"
      if (fromChainId === 56) {
        fromTicker = 'usdcbep20';
      }
      // For Ethereum (chainId 1 or undefined), keep "usdc" as-is
    } else if (fromToken.toUpperCase() === 'HYPE') {
      // SimpleSwap v4 API uses "hype" ticker with "hype" network
      fromTicker = 'hype';
    }
    
    if (toToken.toUpperCase() === 'HYPE') {
      // SimpleSwap v4 API uses "hype" ticker with "hype" network
      toTicker = 'hype';
    }
    
    console.log('[SimpleSwap] Create exchange ticker mapping:', { fromTicker, toTicker, fromChainId, toChainId });

    // SimpleSwap create exchange - direct API call (proxy IP blocked, affiliate key is safe to expose)
    // AFFILIATE API KEY tracks 0.4% commission paid monthly in USDT
    // For HYPE, use v4 API; for others, use old API
    console.log('[SimpleSwap] Creating exchange via direct API:', { fromTicker, toTicker, amount, destinationAddress });
    
    const fromNetwork = getSimpleSwapNetworkSuffix(fromChainId) || fromTicker;
    const toNetwork = getSimpleSwapNetworkSuffix(toChainId) || toTicker;
    
    let url: string;
    let requestBody: any;
    
    // Use v4 API for HYPE and ICP (old API rejects their addresses)
    // Use old API for other tokens
    // NOTE: SimpleSwap old API doesn't support BTC → MATIC or BTC → AVAX
    // These pairs will fail and should fall back to ChangeNOW
    if (fromTicker === 'hype' || toTicker === 'hype' || fromTicker === 'icp' || toTicker === 'icp') {
      url = `https://simpleswap.io/api/v4/exchanges-new?api_key=${encodeURIComponent(API_KEYS.simpleswap)}`;
      requestBody = {
        amount: amount.toString(),
        fixed: false,
        networkFrom: fromNetwork || (fromTicker === 'hype' ? 'hype' : fromTicker === 'icp' ? 'icp' : fromTicker),
        networkTo: toNetwork || (toTicker === 'hype' ? 'hype' : toTicker === 'icp' ? 'icp' : toTicker),
        tickerFrom: fromTicker,
        tickerTo: toTicker,
        addressTo: destinationAddress,
      };
    } else {
      // Old API for other tokens
      // NOTE: SimpleSwap old API has known issues with:
      // - BTC → MATIC (returns "matic_matic" error - not supported)
      // - BTC → AVAX (address validation fails - not supported)
      // - BTC → LINK/WETH (intermittent 500 errors - retry may help)
      url = `https://api.simpleswap.io/create_exchange?api_key=${encodeURIComponent(API_KEYS.simpleswap)}`;
      requestBody = {
        currency_from: fromTicker,
        currency_to: toTicker,
        amount: amount.toString(),
        address_to: destinationAddress,
        fixed: false
      };
    }
    
    // Check for null, undefined, empty string, or whitespace-only strings
    const hasValidWalletAddress = walletAddress && 
                                  typeof walletAddress === 'string' && 
                                  walletAddress.trim().length > 0 &&
                                  walletAddress !== 'null' &&
                                  walletAddress !== 'undefined';
    
    if (hasValidWalletAddress) {
      requestBody.user_refund_address = walletAddress.trim();
    } else {
      // Explicitly ensure user_refund_address is NOT in the request body
      // This prevents SimpleSwap from receiving an invalid/empty refund address
      delete requestBody.user_refund_address;
    }
    
    // Log for debugging - log the ACTUAL request body being sent
    const requestBodyJson = JSON.stringify(requestBody);
    console.log('[SimpleSwap] Create request body JSON:', requestBodyJson);
    console.log('[SimpleSwap] Request body object:', {
      ...requestBody,
      user_refund_address: requestBody.user_refund_address ? '***SET***' : '***OMITTED***',
      walletAddressProvided: !!walletAddress,
      walletAddressType: typeof walletAddress,
      walletAddressValue: walletAddress,
      walletAddressLength: walletAddress?.length,
      walletAddressTrimmed: walletAddress?.trim()?.length
    });
    
    const resp = await Promise.race([
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: requestBodyJson
      }),
      createTimeout(API_TIMEOUT)
    ]);
    
    let data: any;
    const responseText = await resp.text().catch(() => '');
    
    // Parse response
    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch {
      data = {};
    }
    
    console.log('[SimpleSwap] API response:', { status: resp.status, statusText: resp.statusText, data });
    
    if (!resp.ok) {
      const errorText = data.message || data.error || data.description || responseText || 'Unknown error';
      
      // For HYPE/ICP swaps, if v4 API fails, try old API as fallback
      if (resp.status === 400 && (fromTicker === 'hype' || toTicker === 'hype' || fromTicker === 'icp' || toTicker === 'icp')) {
        console.log('[SimpleSwap] v4 API failed, trying old API as fallback...');
        try {
          // Use old API format
          const oldApiUrl = `https://api.simpleswap.io/create_exchange?api_key=${encodeURIComponent(API_KEYS.simpleswap)}`;
          const oldApiBody = {
            currency_from: fromTicker,
            currency_to: toTicker,
            amount: amount.toString(),
            address_to: destinationAddress,
            fixed: false
          };
          
          // Include refund address if provided
          if (walletAddress && typeof walletAddress === 'string' && walletAddress.trim().length > 0) {
            oldApiBody.user_refund_address = walletAddress.trim();
          }
          
          const oldApiResp = await Promise.race([
            fetch(oldApiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(oldApiBody)
            }),
            createTimeout(API_TIMEOUT)
          ]);
          
          const oldApiText = await oldApiResp.text().catch(() => '');
          let oldApiData: any;
          try {
            oldApiData = oldApiText ? JSON.parse(oldApiText) : {};
          } catch {
            oldApiData = {};
          }
          
          if (oldApiResp.ok && oldApiData.id) {
            console.log('[SimpleSwap] Old API fallback succeeded!');
            // Use old API response format
            const exchangeId = oldApiData.id;
            const depositAddress = oldApiData.address_from || oldApiData.addressFrom;
            
            return {
              txHash: exchangeId,
              success: true,
              depositAddress: depositAddress || undefined,
              exchangeId: exchangeId,
            };
          }
        } catch (fallbackError) {
          console.warn('[SimpleSwap] Old API fallback also failed:', fallbackError);
        }
      }
      
      throw new Error(`SimpleSwap API returned status ${resp.status}: ${errorText}`);
    }
    
    // Handle both old API and v4 API response formats
    // v4 API returns: { result: { publicId: "...", isCustomer: true } }
    // Old API returns: { id: "...", address_from: "..." }
    const resultData = data.result || {};
    let exchangeId = data.id || data.exchangeId || data.exchange_id || resultData.id || resultData.exchangeId;
    // Check ALL possible deposit address fields (v4 API might use different field names)
    let depositAddress = data.address_from || data.addressFrom || data.deposit_address || data.depositAddress || data.payin_address || data.payinAddress ||
                        resultData.addressFrom || resultData.address_from || resultData.depositAddress || resultData.deposit_address ||
                        resultData.payinAddress || resultData.payin_address || resultData.payin || resultData.address;
    
    // For v4 API, if we got publicId but no exchange ID, use publicId as exchange identifier
    // v4 API uses publicId as the exchange identifier - it's the public-facing ID
    if (!exchangeId && resultData.publicId && (fromTicker === 'hype' || toTicker === 'hype' || fromTicker === 'icp' || toTicker === 'icp')) {
      console.log('[SimpleSwap] v4 API returned publicId, using it as exchange identifier:', resultData.publicId);
      exchangeId = resultData.publicId;
      // For v4 API, try to query exchange details with publicId to get deposit address
      if (!depositAddress) {
        try {
          console.log('[SimpleSwap] Attempting to fetch exchange details with publicId...');
          const detailsUrl = `https://simpleswap.io/api/v4/exchanges/${resultData.publicId}?api_key=${encodeURIComponent(API_KEYS.simpleswap)}`;
          const detailsResp = await Promise.race([
            fetch(detailsUrl),
            createTimeout(API_TIMEOUT)
          ]);
          if (detailsResp.ok) {
            const detailsData = await detailsResp.json();
            console.log('[SimpleSwap] Exchange details response:', detailsData);
            const detailsResult = detailsData.result || {};
            // Check ALL possible deposit address fields in exchange details response
            depositAddress = detailsData.addressFrom || detailsData.address_from || detailsData.depositAddress || detailsData.deposit_address ||
                           detailsData.payinAddress || detailsData.payin_address || detailsData.payin || detailsData.address ||
                           detailsResult.addressFrom || detailsResult.address_from || detailsResult.depositAddress || detailsResult.deposit_address ||
                           detailsResult.payinAddress || detailsResult.payin_address || detailsResult.payin || detailsResult.address;
          }
        } catch (detailsError) {
          console.warn('[SimpleSwap] Could not fetch exchange details:', detailsError);
        }
      }
    }

    console.log('[SimpleSwap] API Response:', {
      exchangeId,
      depositAddress,
      fullResponse: data,
    });

    if (!exchangeId) {
      throw new Error('SimpleSwap did not return exchange ID');
    }

    if (!depositAddress) {
      console.warn('[SimpleSwap] No deposit address in response:', data);
      // For v4 API, deposit address might not be in initial response
      // Still return success - user may need to check exchange status
      if (fromTicker === 'hype' || toTicker === 'hype' || fromTicker === 'icp' || toTicker === 'icp') {
        console.warn('[SimpleSwap] v4 API: Exchange created with publicId but no deposit address in response. User may need to check exchange status on SimpleSwap website.');
      }
    } else {
      console.log('[SimpleSwap] Deposit address received:', depositAddress);
      // Don't show toast here - let the modal handle it
      // toast.info('Send Funds to Complete Swap', {
      //   description: `Send ${amount} ${fromToken} to: ${depositAddress.substring(0, 20)}...\nExchange ID: ${exchangeId}`,
      //   duration: 15000,
      // });
      
      console.log('SimpleSwap Exchange Created (0.4% commission earned!):', {
        exchangeId,
        depositAddress,
        commission: (amount * 0.004).toFixed(6),
      });
    }
    
    return {
      txHash: exchangeId,
      success: true,
      depositAddress: depositAddress || undefined,
      exchangeId: exchangeId,
    };
  } catch (error: any) {
    console.error('SimpleSwap swap execution error:', error);
    throw new Error(error.message || 'Failed to execute SimpleSwap swap');
  }
}

// Execute swap via ChangeNOW (with KYC check)
async function executeChangeNOWSwap(
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  destinationAddress: string,
  fromChainId: number = 1,
  toChainId: number = 1
): Promise<{ txHash: string; success: boolean; depositAddress?: string; exchangeId?: string; payinExtraId?: string }> {
  const provider = getProviderByName('ChangeNOW');
  if (!provider) {
    throw new Error('ChangeNOW provider not found');
  }

  if (!API_KEYS.changenow) {
    throw new Error('ChangeNOW API key not configured');
  }

  // Validate destination address is provided (required for cross-chain)
  if (!destinationAddress) {
    throw new Error('Destination address is required for ChangeNOW swaps');
  }

  // Validate that destination address is NOT a placeholder
  if (isPlaceholderAddress(destinationAddress, toToken)) {
    throw new Error('Please enter a real destination address. Placeholder addresses cannot be used for actual swaps.');
  }

  try {
    const fromTicker = fromToken.toLowerCase();
    const toTicker = toToken.toLowerCase();

    // Normalize destination address for ChangeNOW API
    // ChangeNOW doesn't accept addresses with certain prefixes (kaspa:, bitcoincash:, etc.)
    let normalizedAddress = destinationAddress;
    
    // Kaspa: strip "kaspa:" prefix
    if (toTicker === 'kas' && destinationAddress.toLowerCase().startsWith('kaspa:')) {
      normalizedAddress = destinationAddress.replace(/^kaspa:/i, '');
      console.log('[ChangeNOW] Normalized Kaspa address:', { original: destinationAddress, normalized: normalizedAddress });
    }
    
    // Bitcoin Cash: strip "bitcoincash:" prefix (CashAddr format)
    if (toTicker === 'bch' && destinationAddress.toLowerCase().startsWith('bitcoincash:')) {
      normalizedAddress = destinationAddress.replace(/^bitcoincash:/i, '');
      console.log('[ChangeNOW] Normalized Bitcoin Cash address:', { original: destinationAddress, normalized: normalizedAddress });
    }
    
    // Note: XRP and XLM use separate extraId fields for destination tags/memos,
    // not address prefixes, so they don't need normalization here

    // ChangeNOW v2 createTransaction via VPS proxy (bypasses IC HTTP outcall issues)
    // Format: JSON-RPC 2.0 POST to /v2/exchange with createTransaction method
    const proxyUrl = buildProxyUrl('changenow/create');
    let data: any;
    
    if (proxyUrl) {
      try {
        // Get network names for ChangeNOW
        let fromNetwork = getChangeNOWNetworkName(fromChainId);
        let toNetwork = getChangeNOWNetworkName(toChainId);
        
        // For HYPE token, always use "hype" as network
        if (fromToken.toUpperCase() === 'HYPE') {
          fromNetwork = 'hype';
        }
        if (toToken.toUpperCase() === 'HYPE') {
          toNetwork = 'hype';
        }
        
    // For BNB token, always use "bsc" as network
    if (fromToken.toUpperCase() === 'BNB') {
      fromNetwork = 'bsc';
    }
    if (toToken.toUpperCase() === 'BNB') {
      toNetwork = 'bsc';
    }
    
    // For AVAX token, always use "cchain" as network (Avalanche C-Chain)
    if (fromToken.toUpperCase() === 'AVAX') {
      fromNetwork = 'cchain';
    }
    if (toToken.toUpperCase() === 'AVAX') {
      toNetwork = 'cchain';
    }
    
    // For TRX token, ChangeNOW auto-uses "trx" network - don't override
    // ChangeNOW automatically detects TRX network from currency ticker
    // Only set if not already set by getChangeNOWNetworkName
    if ((fromToken.toUpperCase() === 'TRX' || fromToken.toUpperCase() === 'TRON') && !fromNetwork) {
      fromNetwork = 'trx';
    }
    if ((toToken.toUpperCase() === 'TRX' || toToken.toUpperCase() === 'TRON') && !toNetwork) {
      toNetwork = 'trx';
    }
    
    // For native tokens that use their own network names on ChangeNOW
    if (fromToken.toUpperCase() === 'ADA') {
          fromNetwork = 'ada';
        }
        if (toToken.toUpperCase() === 'ADA') {
          toNetwork = 'ada';
        }
        if (fromToken.toUpperCase() === 'ZEC') {
          fromNetwork = 'zec';
        }
        if (toToken.toUpperCase() === 'ZEC') {
          toNetwork = 'zec';
        }
        if (fromToken.toUpperCase() === 'DASH') {
          fromNetwork = 'dash';
        }
        if (toToken.toUpperCase() === 'DASH') {
          toNetwork = 'dash';
        }
        if (fromToken.toUpperCase() === 'XMR') {
          fromNetwork = 'xmr';
        }
        if (toToken.toUpperCase() === 'XMR') {
          toNetwork = 'xmr';
        }
        
        const params: any = {
          fromCurrency: fromTicker,
          toCurrency: toTicker,
          amountFrom: amount.toString(),
          address: normalizedAddress
        };
        
        // Add network parameters if available (required for multi-chain tokens)
        if (fromNetwork) {
          params.fromNetwork = fromNetwork;
        }
        if (toNetwork) {
          params.toNetwork = toNetwork;
        }
        
        const jsonRpcBody = {
          jsonrpc: '2.0',
          id: Date.now().toString(),
          method: 'createTransaction',
          params
        };
        
        console.log('[ChangeNOW] Creating transaction via VPS proxy:', { fromTicker, toTicker, amount, fromNetwork, toNetwork, destinationAddress: normalizedAddress });
        const resp = await Promise.race([
          fetch(proxyUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(jsonRpcBody)
          }),
          createTimeout(API_TIMEOUT)
        ]);
        
        if (!resp.ok) {
          const errorText = await resp.text().catch(() => '');
          console.error('[ChangeNOW] VPS proxy error response:', { status: resp.status, body: errorText });
          
          // Parse error response to check for specific error types
          try {
            const errorData = JSON.parse(errorText);
            
            // Check for minimum amount errors
            if (errorData.error === 'out_of_range' || errorData.message?.includes('less then minimal') || errorData.message?.includes('Amount is less')) {
              const minAmount = errorData.message?.match(/minimal[:\s]+([\d.]+)/i)?.[1];
              const minAmountError = new Error(`Amount is below minimum: ${minAmount ? `${minAmount} ${fromToken}` : 'minimum amount required'}`);
              throw minAmountError;
            }
            
            // Check for invalid address errors - ChangeNOW's quote endpoint may return quotes
            // but createTransaction can reject addresses due to strict validation or lack of support
            if (errorData.error === 'not_valid_address' && errorData.message?.includes('not valid')) {
              // Extract token from error message if possible
              const tokenMatch = errorData.message.match(/not valid (\w+)/i);
              const tokenName = tokenMatch ? tokenMatch[1] : toToken;
              const unsupportedError = new Error(`ChangeNOW returns quotes for ${tokenName} but rejects the address format. This may indicate they don't support ${tokenName} swaps or have strict address validation. Please use SimpleSwap instead.`);
              throw unsupportedError;
            }
          } catch (parseError: any) {
            // If it's one of our specific errors, rethrow it
            if (parseError.message?.includes('Amount is below minimum') || parseError.message?.includes('may not support')) {
              throw parseError;
            }
            // If parsing fails, continue with original error
          }
          
          throw new Error(`VPS proxy returned status ${resp.status}: ${errorText.substring(0, 200)}`);
        }
        data = await resp.json();
        console.log('[ChangeNOW] VPS proxy response:', data);
      } catch (vpsError: any) {
        // Don't fall back to backend for minimum amount errors
        if (vpsError.message?.includes('Amount is below minimum')) {
          throw vpsError;
        }
        console.warn('[ChangeNOW] VPS proxy failed, falling back to backend:', vpsError);
        // Fall through to backend fallback
      }
    }
    
    // Fallback to backend (if proxy not configured or failed)
    if (!data) {
      const { backend } = await import('../declarations/backend');
      if (!backend) throw new Error('Backend canister not available');
      const result = await backend.proxyChangeNOWCreate(
        fromTicker,
        toTicker,
        amount.toString(),
        normalizedAddress,
        API_KEYS.changenow
      );
      
      if ('err' in result) {
        const errorText = result.err;
        // Check if it's a KYC error
        if (errorText.includes('KYC') || errorText.includes('kyc') || errorText.includes('403')) {
          throw new Error('KYC_REQUIRED: This transaction requires KYC verification. You can cancel this swap.');
        }
        throw new Error(errorText);
      }

      data = JSON.parse(result.ok);
    }

    // Parse ChangeNOW v2 JSON-RPC response
    // Format: { jsonrpc: "2.0", id: "...", result: { id: "...", payinAddress: "...", ... } }
    // Or error: { jsonrpc: "2.0", id: "...", error: { code: ..., message: ... } }
    if (data.error) {
      // Check if it's a KYC error
      if (data.error.code === 403 || data.error.message?.includes('KYC') || data.error.message?.includes('kyc')) {
        throw new Error('KYC_REQUIRED: This transaction requires KYC verification. You can cancel this swap.');
      }
      throw new Error(data.error.message || `ChangeNOW API error: ${JSON.stringify(data.error)}`);
    }
    
    const resultData = data.result || data;
    // ChangeNOW v2 returns transaction details in result object
    const transactionId = resultData.id || resultData.transactionId || resultData.uuid;
    const depositAddress = resultData.payinAddress || resultData.payin_address || resultData.address || resultData.address_from;
    const payinExtraId = resultData.payinExtraId || resultData.memo || resultData.tag; // For memo-based coins (XRP, XLM, etc.)

    console.log('[ChangeNOW] API Response:', {
      transactionId,
      depositAddress,
      payinExtraId,
      fullResponse: resultData,
    });

    if (!transactionId) {
      throw new Error('ChangeNOW did not return transaction ID');
    }

    // For ChangeNOW, user needs to send funds to depositAddress
    // The transaction ID is used to track the swap
    // Don't show toast here - let the modal handle it
    if (depositAddress) {
      console.log('[ChangeNOW] Deposit address received:', depositAddress);
      // toast.info('Send Funds to Deposit Address', {
      //   description: `Send ${amount} ${fromTicker} to: ${depositAddress.substring(0, 20)}...\nTransaction ID: ${transactionId.substring(0, 8)}...`,
      //   duration: 10000,
      // });
      
      // Also log for debugging
      console.log('ChangeNOW Swap Created:', {
        transactionId,
        depositAddress,
        from: fromTicker,
        to: toTicker,
        amount,
      });
    } else {
      console.warn('[ChangeNOW] No deposit address in response:', data);
    }
    
    // Return the transaction ID as txHash for tracking
    return {
      txHash: transactionId,
      success: true,
      depositAddress: depositAddress || undefined,
      exchangeId: transactionId,
      payinExtraId: payinExtraId || undefined, // Memo/tag for memo-based coins (XRP, XLM, etc.)
    };
  } catch (error: any) {
    console.error('ChangeNOW swap execution error:', error);
    
    // Preserve KYC error message
    if (error.message.includes('KYC_REQUIRED')) {
      throw error;
    }
    
    throw new Error(error.message || 'Failed to execute ChangeNOW swap');
  }
}

// Execute swap through selected provider with enhanced error handling
export async function executeProviderSwap(
  provider: string,
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  fromChainId: number = 1,
  toChainId?: number,
  destinationAddress?: string
): Promise<{ txHash: string; success: boolean; depositAddress?: string; exchangeId?: string; payinExtraId?: string }> {
  try {
    let result: { txHash: string; success: boolean; depositAddress?: string; exchangeId?: string; payinExtraId?: string };
    
    // For cross-chain swaps, use destinationAddress if provided, otherwise use walletAddress
    const recipientAddress = destinationAddress || walletAddress;
    const destChainId = toChainId || fromChainId;

    // Manual swap providers that only return deposit addresses (not actual swap execution)
    const isManualSwap = MANUAL_SWAP_PROVIDERS.includes(provider as typeof MANUAL_SWAP_PROVIDERS[number]);

    // Route to appropriate aggregator execution function
    switch (provider) {
      case '0x Protocol':
        result = await execute0xSwap(fromToken, toToken, amount, walletAddress, fromChainId);
        break;
      case 'Velora (ParaSwap)':
      case 'Paraswap':
        result = await executeParaswapSwap(fromToken, toToken, amount, walletAddress, fromChainId);
        break;
      case '1inch Aggregator':
        result = await execute1inchSwap(fromToken, toToken, amount, walletAddress, fromChainId);
        break;
      case 'KyberSwap':
        result = await executeKyberSwapSwap(fromToken, toToken, amount, walletAddress, fromChainId);
        break;
      case 'OpenOcean':
        result = await executeOpenOceanSwap(fromToken, toToken, amount, walletAddress, fromChainId);
        break;
      // case 'Ravencoin Bridge': // REMOVED - Not usable
      //   result = await executeRavencoinBridgeSwap(fromToken, toToken, amount, walletAddress);
      //   break;
      case 'Wormhole':
        // For cross-chain swaps, use destinationAddress and both chain IDs
        result = await executeWormholeSwap(fromToken, toToken, amount, walletAddress, fromChainId, destChainId, recipientAddress);
        break;
      case 'Across Protocol':
        // For cross-chain swaps, use destinationAddress and both chain IDs
        result = await executeAcrossSwap(fromToken, toToken, amount, walletAddress, fromChainId, destChainId, recipientAddress);
        break;
      case 'SimpleSwap':
        // SimpleSwap AFFILIATE - Earns you 0.4% commission automatically!
        {
          const resolvedDest =
            destinationAddress ||
            (isEVMCompatibleToken(toToken, destChainId) && isValidEVMAddress(walletAddress) ? walletAddress : undefined);
          if (!resolvedDest) {
            throw new Error('Destination address is required for SimpleSwap swaps');
          }
          result = await executeSimpleSwapSwap(fromToken, toToken, amount, walletAddress, resolvedDest, fromChainId, destChainId);
        }
        break;
      case 'ChangeNOW':
        // ChangeNOW requires destination address for cross-chain swaps
        {
          const resolvedDest =
            destinationAddress ||
            (isEVMCompatibleToken(toToken, destChainId) && isValidEVMAddress(walletAddress) ? walletAddress : undefined);
          if (!resolvedDest) {
            throw new Error('Destination address is required for ChangeNOW swaps');
          }
          result = await executeChangeNOWSwap(fromToken, toToken, amount, walletAddress, resolvedDest, fromChainId, destChainId);
        }
        break;
      case 'ICPSwap':
      case 'Sonic':
        // ICP DEX swaps require user's ICP wallet identity
        // Get identity from useInternetIdentity hook (needs to be passed from component)
        // For now, return error - implementation requires canister IDL
        throw new Error('ICP DEX swap execution requires canister IDL - implementation in progress');
        /*
        // Actual implementation would look like:
        const { executeICPDEXSwap } = await import('./icpDex');
        const { useInternetIdentity } = await import('../hooks/useInternetIdentity');
        const { identity } = useInternetIdentity();
        
        if (!identity || identity.getPrincipal().isAnonymous()) {
          throw new Error('ICP wallet not connected. Please connect your ICP wallet first.');
        }
        
        const dexResult = await executeICPDEXSwap(
          quote,
          walletAddress, // userPrincipal
          fromToken,
          toToken,
          amount,
          identity,
          provider === 'ICPSwap' ? 'icpswap' : 'sonic'
        );
        
        result = {
          txHash: dexResult.txId || 'pending',
          success: dexResult.success,
        };
        */
        break;
      default:
        throw new Error(`Unknown provider: ${provider}`);
    }

    // Only show "Swap Executed" toast for actual swap executions, not for manual swaps that just return deposit addresses
    // Manual swaps will show "Deposit Address Ready" toast from SwapInterface component
    if (!isManualSwap && !result.depositAddress) {
      toast.success('Swap Executed', {
        description: `Transaction submitted via ${provider}`,
        duration: 4000,
      });
    }

    return result;
  } catch (error: any) {
    console.error('Provider swap execution error:', error);
    
    let errorMessage = 'Failed to execute swap';
    if (error.message.includes('timeout')) {
      errorMessage = 'Transaction timed out';
    } else if (error.message.includes('insufficient')) {
      errorMessage = 'Insufficient liquidity';
    } else if (error.message.includes('rejected')) {
      errorMessage = 'Transaction rejected by user';
    } else if (error.message.includes('NO_PROVIDER')) {
      errorMessage = 'Wallet not connected';
    } else {
      errorMessage = error.message || 'Transaction failed on provider network';
    }

    toast.error('Swap Failed', {
      description: errorMessage,
      duration: 6000,
    });
    
    throw new Error(errorMessage);
  }
}

// Handle liquidity provider errors with specific feedback
export function handleProviderError(error: any, providerName: string): void {
  console.error(`${providerName} error:`, error);

  let description = `${providerName} is temporarily unavailable. Trying alternative routes...`;
  
  if (error.message?.includes('timeout')) {
    description = `${providerName} request timed out. Trying alternative routes...`;
  } else if (error.message?.includes('rate limit')) {
    description = `${providerName} rate limit reached. Trying alternative routes...`;
  }

  toast.error('Provider Error', {
    description,
    duration: 5000,
  });
}

// Export individual aggregator functions for use in router
export {
  // fetch0xQuote, // Temporarily disabled - API-side configuration issue
  // fetchParaswapQuote, // DISABLED until fee collection configured
  // fetchKyberSwapQuote, // DISABLED until fee collection configured
  // fetchOpenOceanQuote, // DISABLED until fee collection configured
  fetch1inchQuote, // ✅ ACTIVE - Can collect fees via smart contract
  // fetchRavencoinBridgeQuote, // REMOVED - Not usable
  // fetchWormholeQuote, // DISABLED until fee collection configured
  fetchAcrossQuote,
  fetchSimpleSwapQuote, // ✅ ACTIVE - Affiliate commission
  fetchChangeNOWQuote,
};

// Export ICP Chain Fusion functions
export { fetchICPChainFusionQuote, isICPChainFusionAvailable, getICPChainFusionPathway } from './icpChainFusion';

// Export ICP DEX functions
export { fetchICPDEXQuote, executeICPDEXSwap, isICPDEXAvailable, getICPDEXPathway } from './icpDex';

